import React, { useCallback, useEffect, useState, useRef, useMemo } from "react";
import axios from "../utils/axiosInstance";
import { useNavigate } from "react-router-dom";
import "bootstrap-icons/font/bootstrap-icons.css";
import "bootstrap/dist/css/bootstrap.min.css";
import { useToast, useConfirmLogout } from "./ConfirmDialog";
import ModuleOverviewPage from "./ModuleOverviewPage";

// IMPORT COMPONENTS
import UmuntuUmwirondoro from "./UmuntuUmwirondoro";
import UrugoUmuryango from "./UrugoUmuryango";
import UmuntuMuRugo from "./UmuntuMuRugo";
import Urubyiruko from "./Urubyiruko";
import Abana from "./Abana";
import AbashesheAkanguhe from "./AbashesheAkanguhe";
import IngoZifiteAmacyimbirane from "./IngoZifiteAmacyimbirane";
import Messaging from "../components/Messaging";
import Broadcasts from "../components/Broadcasts";
import AmatangazoIsibo from "../components/AmatangazoIsibo";
import ReportPage from "../page/ReportPage";
import IbikorwaRaporo from "../page/IbikorwaRaporo";
import ImiberehoMyiza from "../page/ImiberehoMyiza";
import Ingo from "../page/Ingo";
import Ubwishingizi from "../page/Ubwishingizi";
import Ubukungu from "../page/Ubukungu";
import Uburezi from "../page/Uburezi";
import Ubuzima from "../page/Ubuzima";
import Abaturage from "../page/Abaturage";
import AbaturageBafiteInkunguzaleta from "../page/AbaturageBafiteInkunguzaleta";
import UmusanzuEjoheza from "../page/UmusanzuEjoheza";
import GutangaUmutekano from "../page/GutangaUmutekano";
import KurwanyaUmwanda from "../page/KurwanyaUmwanda";
import IsukuIsukura from "../page/IsukuIsukura";
import Attendance from "./Attendance";
import Ibiza from "./Ibiza";

import IsiboStatistics from "./IsiboStatistics";
import ImibareNavigation from "./ImibareNavigation";
import Abashyitsi from "./Abashyitsi";
import AbashyitsiIsibo from "./AbashyitsiIsibo";
import BarizaUmuturage from "./BarizaUmuturage";
import IsiboAttendance from "./IsiboAttendance";
import IbikorwaRemezoForm from "./IbikorwaRemezoForm";
import IbiregoForm from "./IbiregoForm";
import ImicoImyifatire from "./ImicoImyifatire";
import AbanaMuMihanda from "./AbanaMuMihanda";

const API = "";

// ═══════════════════════════════════════════════════════════
// NAVIGATION — two-level structure.
//   Level 1 (sidebar): one flat button per group (icon + label only).
//     Clicking "Dashboard" goes straight to the dashboard; clicking any
//     other group navigates to that group's ModuleOverviewPage.
//   Level 2 (module page): a responsive card grid of that group's items,
//     rendered by <ModuleOverviewPage>. Clicking a card sets `activeTab`
//     to the item's own key, which renders the existing page component
//     exactly as before — no routes, pages, or APIs changed.
// This is the single source of truth for both levels: group label/icon/
// color/description, and each item's key/icon/color/label/description.
// ═══════════════════════════════════════════════════════════
const navGroups = [
  {
    id: "rusange",
    label: "Rusange",
    icon: "grid-1x2-fill",
    color: "#7c3aed",
    description: "Ahabanza h'imikorere yawe ya buri munsi.",
    items: [
      { key: "dashboard", icon: "grid-1x2-fill", label: "Dashboard", color: "#7c3aed", description: "Incamake y'ibikorwa n'imibare y'ingenzi." },
    ],
  },
  {
    id: "amakuru-yingo",
    label: "Amakuru y'Ingo",
    icon: "house-heart-fill",
    color: "#0f766e",
    description: "Reba kandi ucunge amakuru ajyanye n'ingo n'abaturage.",
    items: [
      { key: "abaturage", icon: "bi-people-fill", label: "Abaturage", color: "#818cf8", description: "Reba no gucunga abaturage." },
      { key: "ingo", icon: "bi-house-door-fill", label: "Ingo", color: "#34d399", description: "Reba amakuru y'ingo." },
      { key: "ubwishingizi", icon: "bi-shield-check", label: "Ubwishingizi", color: "#0ea5e9", description: "Ubwishingizi n'ubwishyu bwa Mituweli buri mwaka." },
      { key: "uburezi", icon: "bi-mortarboard-fill", label: "Uburezi", color: "#60a5fa", description: "Amakuru y'uburezi bw'abaturage." },
      { key: "imibereho", icon: "bi-heart-fill", label: "Imibereho Myiza", color: "#f472b6", description: "Kurikirana imibereho myiza." },
      // Clicking this card navigates to the hidden "andi-makuru-ingo" group
      // below (its own overview page), instead of showing its items inline.
      { key: "andi-makuru-ingo", icon: "three-dots", label: "Andi Makuru", color: "#64748b", description: "Andi makuru ajyanye n'ingo n'abaturage." },
    ],
  },
  // Reachable only via the "Andi Makuru" card above — not a top-level
  // sidebar button. `hidden: true` keeps it out of the sidebar and the
  // Quick Actions panel while still letting it render its own overview
  // page (see the render filter, which doesn't check `hidden`).
  {
    id: "andi-makuru-ingo",
    label: "Andi Makuru",
    icon: "three-dots",
    color: "#64748b",
    description: "Andi makuru ajyanye n'ingo n'abaturage.",
    hidden: true,
    items: [
      { key: "abana", icon: "emoji-smile-fill", label: "Abana", color: "#f59e0b", description: "Amakuru y'abana." },
      { key: "ubuzima", icon: "bi-heart-pulse-fill", label: "Ubuzima", color: "#fb7185", description: "Amakuru y'ubuzima bw'abaturage." },
    ],
  },
  {
    id: "isuku",
    label: "Amakuru y'Isuku",
    icon: "stars",
    color: "#0891b2",
    description: "Gucunga isuku n'imyanda mu mudugudu.",
    items: [
      { key: "kurwanyaUmwanda", icon: "bi-trash-fill", label: "Kurwanya Umwanda", color: "#a3e635", description: "Kurwanya umwanda mu mudugudu." },
      { key: "isukuIsukura", icon: "bi-stars", label: "Isuku n'Isukura", color: "#22d3ee", description: "Isuku n'isukura by'urugo." },
    ],
  },
  {
    id: "ubukungu-group",
    label: "Amakuru y'Ubukungu",
    icon: "currency-exchange",
    color: "#b45309",
    description: "Amakuru y'ubukungu n'inkunga za Leta.",
    items: [
      { key: "ubukungu", icon: "bi-currency-exchange", label: "Ubukungu", color: "#fb923c", description: "Amakuru y'ubukungu bw'abaturage." },
      { key: "inkunga", icon: "bi-gift-fill", label: "Inkunga za Leta", color: "#c084fc", description: "Inkunga abaturage bahawe na Leta." },
      { key: "umusanzuEjoheza", icon: "bi-piggy-bank-fill", label: "Umusanzu wa Ejo Heza", color: "#e879f9", description: "Umusanzu wa Ejo Heza." },
    ],
  },
  {
    id: "umutekano",
    label: "Amakuru y'Umutekano",
    icon: "shield-check",
    color: "#7c3aed",
    description: "Kurikirana umutekano n'ibirego.",
    items: [
      { key: "amacyimbirane", icon: "exclamation-triangle-fill", label: "Amakimbirane", color: "#ef4444", description: "Amakimbirane mu mudugudu." },
      { key: "ibirego-kwandika", icon: "file-earmark-text", label: "Kwandika Ikirego", color: "#dc2626", description: "Andika ikirego gishya." },
      { key: "ibiza", icon: "fire", label: "Ibiza", color: "#ef4444", description: "Kwandika ibiza byabaye." },
    ],
  },
  {
    id: "raporo-group",
    label: "Gukora Raporo",
    icon: "file-earmark-text-fill",
    color: "#1d4ed8",
    description: "Kwandika no gukurikirana raporo n'ibikorwa.",
    items: [
      { key: "attendance-mgmt", icon: "clipboard-check-fill", label: "Attendance", color: "#0047AB", description: "Kurikirana ubwitabire." },
      { key: "imico-imyifatire", icon: "award-fill", label: "Imico n'Imyifatire", color: "#0f766e", description: "Raporo y'imico n'imyifatire." },
      { key: "raporo", icon: "file-earmark-text-fill", label: "Kwandika Raporo", color: "#6366f1", description: "Andika raporo nshya." },
      { key: "raporo-zanjye", icon: "list-check", label: "Raporo Nakoze", color: "#4338ca", description: "Reba raporo wanditse n'aho zigeze (byoherejwe, byemejwe...)." },
      { key: "abashyitsi", icon: "person-walking", label: "Abashyitsi", color: "#06b6d4", description: "Reba abashyitsi." },
      { key: "abana-mu-mihanda", icon: "signpost-split", label: "Abana Bari mu Mihanda", color: "#dc2626", description: "Abana bari mu mihanda." },
    ],
  },
  {
    id: "kohereza-ubutumwa",
    label: "Kohereza Ubutumwa",
    icon: "chat-dots-fill",
    color: "#15803d",
    description: "Ohereza kandi wakire ubutumwa n'amatangazo.",
    items: [
      { key: "messaging", icon: "chat-dots-fill", label: "Ubutumwa", color: "#a78bfa", description: "Ohereza ubutumwa." },
      { key: "amatangazo", icon: "megaphone-fill", label: "Amatangazo", color: "#f97316", description: "Reba no kohereza amatangazo." },
    ],
  },
  // Everything below existed before but doesn't have a clean home in the
  // simplified 9-category list — kept here (not deleted) rather than
  // guessed into a category above. Move these anytime.
  {
    id: "ibindi",
    label: "Ibindi",
    icon: "three-dots",
    color: "#475569",
    description: "Ibindi bikorwa n'amakuru adafite itsinda ryihariye.",
    items: [
      { key: "imibare", icon: "bar-chart-fill", label: "Imibare", color: "#22d3ee", description: "Imibare rusange." },
      { key: "gutangaUmutekano", icon: "bi-shield-lock-fill", label: "Gutanga Umutekano", color: "#38bdf8", description: "Gutanga umutekano." },
      { key: "ibikorwa-remezo", icon: "building-gear", label: "Ibikorwa Remezo", color: "#0ea5e9", description: "Ibikorwa remezo by'umudugudu." },
      { key: "bariza-umuturage", icon: "chat-square-quote-fill", label: "Bariza Umuturage", color: "#f59e0b", description: "Bariza umuturage ibibazo." },
    ],
  },
  // "Konti" (Account) has just Sohoka (logout) — rendered as its own
  // pinned footer button below, not in the scrollable list, so it's
  // always reachable without scrolling. See `dx-sidebar-foot`.
];

// Flat list derived from the groups above, kept as the single source
// of truth for item metadata (active-item lookup, mobile shortcuts, etc.)
const navItems = navGroups.flatMap((g) => g.items);

// The mobile bottom bar only has room for a handful of shortcuts.
// Keep the exact same 5 shortcuts (and order) it already had before
// this reorganization — independent of the sidebar's new group order.
const BOTTOM_NAV_KEYS = ["dashboard", "ibirego-kwandika", "imico-imyifatire", "abana-mu-mihanda", "abaturage"];
const bottomNavItems = BOTTOM_NAV_KEYS.map((key) => navItems.find((item) => item.key === key)).filter(Boolean);

const getIconClassName = (icon) => `bi ${icon.startsWith("bi-") ? icon : `bi-${icon}`}`;

// Each nav item already carries its own accent `color`, but it used to sit
// unused as a dead CSS variable — every icon rendered the same generic
// translucent-white badge regardless of item. This turns that per-item
// color into an actual muted icon tint so the sidebar reads with more
// visual identity, without touching the item data itself.
const hexToRgba = (hex, alpha = 1) => {
  const h = (hex || "").replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const n = parseInt(full, 16);
  if (Number.isNaN(n)) return `rgba(255,255,255,${alpha})`;
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
};

// ═══════════════════════════════════════════════════════════
// SVG LINE CHART
// ═══════════════════════════════════════════════════════════
function SvgLineChart({ data, labels, color = "#0047AB", height = 200 }) {
  if (!data || data.length === 0) return <div className="dx-empty-chart">Nta makuru</div>;
  const max = Math.max(...data) * 1.15 || 1;
  const w = 600,
    h = height;
  const p = { top: 20, right: 20, bottom: 30, left: 10 };
  const cw = w - p.left - p.right;
  const ch = h - p.top - p.bottom;
  const pts = data.map((v, i) => ({
    x: p.left + (data.length > 1 ? (i / (data.length - 1)) * cw : cw / 2),
    y: p.top + ch - (v / max) * ch,
  }));
  const pathD = pts.map((pt, i) => `${i === 0 ? "M" : "L"} ${pt.x} ${pt.y}`).join(" ");
  const areaD = `${pathD} L ${pts[pts.length - 1].x} ${p.top + ch} L ${pts[0].x} ${p.top + ch} Z`;
  const gridLines = 4;
  return (
    <div style={{ width: "100%", overflowX: "auto" }}>
      <svg viewBox={`0 0 ${w} ${h}`} style={{ width: "100%", minWidth: 250, height: "auto" }}>
        <defs>
          <linearGradient id={`lg-${color.replace("#", "")}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.3" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
        {Array.from({ length: gridLines }, (_, i) => {
          const y = p.top + (ch / (gridLines - 1)) * i;
          const val = Math.round(max - (max / (gridLines - 1)) * i);
          return (
            <g key={i}>
              <line x1={p.left} y1={y} x2={w - p.right} y2={y} stroke="rgba(0,71,171,0.08)" strokeWidth="1" />
              <text x={p.left - 4} y={y + 4} textAnchor="end" fontSize="10" fill="rgba(0,0,0,0.3)" fontFamily="Nunito Sans">
                {val}
              </text>
            </g>
          );
        })}
        {labels.map((label, i) => {
          const x = p.left + (labels.length > 1 ? (i / (labels.length - 1)) * cw : cw / 2);
          return (
            <text key={i} x={x} y={h - 6} textAnchor="middle" fontSize="9" fill="rgba(0,0,0,0.4)" fontFamily="Nunito Sans">
              {label}
            </text>
          );
        })}
        <path d={areaD} fill={`url(#lg-${color.replace("#", "")})`} />
        <path d={pathD} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        {pts.map((pt, i) => (
          <g key={i}>
            <circle cx={pt.x} cy={pt.y} r="4" fill="#f0f4f8" stroke={color} strokeWidth="2.5" />
            <circle cx={pt.x} cy={pt.y} r="12" fill={color} opacity="0">
              <title>
                {labels[i]}: {data[i]}
              </title>
            </circle>
          </g>
        ))}
      </svg>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// SVG BAR CHART
// ═══════════════════════════════════════════════════════════
function SvgBarChart({ data, labels, color = "#0047AB", height = 200 }) {
  if (!data || data.length === 0) return <div className="dx-empty-chart">Nta makuru</div>;
  const max = Math.max(...data) * 1.2 || 1;
  const w = 600,
    h = height;
  const p = { top: 20, right: 20, bottom: 30, left: 10 };
  const cw = w - p.left - p.right;
  const ch = h - p.top - p.bottom;
  const bw = (cw / data.length) * 0.5;
  const gap = cw / data.length;
  return (
    <div style={{ width: "100%", overflowX: "auto" }}>
      <svg viewBox={`0 0 ${w} ${h}`} style={{ width: "100%", minWidth: 250, height: "auto" }}>
        <defs>
          <linearGradient id={`bg-${color.replace("#", "")}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} />
            <stop offset="100%" stopColor={color} stopOpacity="0.4" />
          </linearGradient>
        </defs>
        {Array.from({ length: 5 }, (_, i) => {
          const y = p.top + (ch / 4) * i;
          return <line key={i} x1={p.left} y1={y} x2={w - p.right} y2={y} stroke="rgba(0,71,171,0.06)" strokeWidth="1" />;
        })}
        {data.map((val, i) => {
          const barH = (val / max) * ch;
          const x = p.left + i * gap + (gap - bw) / 2;
          const y = p.top + ch - barH;
          return (
            <g key={i}>
              <rect x={x} y={y} width={bw} height={barH} rx="6" fill={`url(#bg-${color.replace("#", "")})`} opacity="0.9">
                <title>
                  {labels[i]}: {val}
                </title>
              </rect>
              <text x={x + bw / 2} y={h - 6} textAnchor="middle" fontSize="9" fill="rgba(0,0,0,0.4)" fontFamily="Nunito Sans">
                {labels[i]}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// SVG PIE CHART
// ═══════════════════════════════════════════════════════════
function SvgPieChart({ data, size = 180 }) {
  if (!data || data.length === 0 || data.every((d) => d.value === 0)) return <div className="dx-empty-chart">Nta makuru</div>;
  const total = data.reduce((s, d) => s + d.value, 0) || 1;
  const r = 70,
    cx = size / 2,
    cy = size / 2;
  let cum = -90;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 24, flexWrap: "wrap", justifyContent: "center" }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {data.map((slice, i) => {
          const angle = (slice.value / total) * 360;
          if (angle < 0.5) return null;
          const startA = cum;
          const endA = cum + angle;
          cum = endA;
          const sr = (startA * Math.PI) / 180,
            er = (endA * Math.PI) / 180;
          const x1 = cx + r * Math.cos(sr),
            y1 = cy + r * Math.sin(sr);
          const x2 = cx + r * Math.cos(er),
            y2 = cy + r * Math.sin(er);
          const la = angle > 180 ? 1 : 0;
          const ma = (((startA + endA) / 2) * Math.PI) / 180;
          const tx = cx + r * 0.6 * Math.cos(ma),
            ty = cy + r * 0.6 * Math.sin(ma);
          return (
            <g key={i}>
              <path d={`M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${la} 1 ${x2} ${y2} Z`} fill={slice.color} opacity="0.85" stroke="#0a0a12" strokeWidth="2">
                <title>
                  {slice.label}: {slice.value} ({Math.round((slice.value / total) * 100)}%)
                </title>
              </path>
              {angle > 30 && (
                <text x={tx} y={ty} textAnchor="middle" dominantBaseline="central" fontSize="11" fontWeight="700" fill="#fff" fontFamily="Nunito Sans">
                  {Math.round((slice.value / total) * 100)}%
                </text>
              )}
            </g>
          );
        })}
        <circle cx={cx} cy={cy} r={r * 0.42} fill="#f0f4f8" />
        <text x={cx} y={cy - 4} textAnchor="middle" fontSize="15" fontWeight="800" fill="#0d1b2a" fontFamily="Nunito Sans">
          {total}
        </text>
        <text x={cx} y={cy + 10} textAnchor="middle" fontSize="8" fontWeight="600" fill="rgba(0,0,0,0.4)" fontFamily="Nunito Sans">
          Yose
        </text>
      </svg>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {data
          .filter((d) => d.value > 0)
          .map((s, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ width: 10, height: 10, borderRadius: 3, background: s.color, flexShrink: 0 }} />
              <span style={{ fontSize: 12, color: "#4a5568", fontFamily: "Nunito Sans" }}>{s.label}</span>
              <span style={{ fontSize: 12, fontWeight: 700, color: "#0d1b2a", fontFamily: "Nunito Sans" }}>{s.value}</span>
            </div>
          ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// ANIMATED STAT VALUE — count-up on change
// ═══════════════════════════════════════════════════════════
function AnimatedStatValue({ value }) {
  const [display, setDisplay] = useState(0);
  const prevValue = useRef(0);
  useEffect(() => {
    const start = prevValue.current;
    const end = Number(value) || 0;
    if (start === end) {
      setDisplay(end);
      return;
    }
    const duration = 700;
    let startTime = null;
    let raf;
    const step = (ts) => {
      if (!startTime) startTime = ts;
      const progress = Math.min((ts - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(start + (end - start) * eased));
      if (progress < 1) raf = requestAnimationFrame(step);
      else prevValue.current = end;
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value]);
  return display;
}

// Which group (by id) a given nav key lives in — used both to highlight
// the right sidebar button and to know which items to show underneath
// when navigating to a group's own key (its ModuleOverviewPage).
const findGroupIdForKey = (key) => navGroups.find((g) => g.items.some((item) => item.key === key))?.id || null;
const findGroupById = (id) => navGroups.find((g) => g.id === id) || null;

// Groups shown as their own sidebar button / Quick Actions row. `hidden`
// groups (e.g. "Andi Makuru") only exist to be reached via a card inside
// a visible group's own overview page — see the "andi-makuru-ingo" group.
const sidebarGroups = navGroups.filter((g) => !g.hidden);

// Walks a possibly-hidden group up to the nearest *visible* sidebar group
// that leads to it — e.g. the hidden "Andi Makuru" group is reached via a
// card inside "Amakuru y'Ingo", so this resolves to "amakuru-yingo" so the
// right sidebar button highlights no matter how deep the active page is.
const sidebarAncestorId = (groupId) => {
  const group = findGroupById(groupId);
  if (!group) return null;
  if (!group.hidden) return group.id;
  const parent = navGroups.find((g) => g.items.some((item) => item.key === groupId));
  return parent ? parent.id : group.id;
};

function DashboardIsibo() {
  const [activeTab, setActiveTab] = useState("dashboard");
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [toast, setToast] = useState(null);
  const { toast: showToast } = useToast();
  const confirmLogoutDialog = useConfirmLogout();
  const [time, setTime] = useState(new Date());
  const navigate = useNavigate();
  const sidebarRef = useRef(null);

  // Decode user info from JWT
  const userInfo = useMemo(() => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return { name: "Isibo", initials: "IS" };
      const payload = JSON.parse(atob(token.split(".")[1]));
      return { name: payload.fullname || "Isibo", initials: "IS", role: payload.role || "Isibo" };
    } catch { return { name: "Isibo", initials: "IS" }; }
  }, []);
  const [userName, setUserName] = useState(userInfo.name);

  const [urubyiruko, setUrubyiruko] = useState([]);
  const [abana, setAbana] = useState([]);
  const [abasheshe, setAbasheshe] = useState([]);
  const [abantu, setAbantu] = useState([]);
  const [ingo, setIngo] = useState([]);

  // Dashboard extras state
  const [darkMode, setDarkMode] = useState(() => {
    try {
      return localStorage.getItem("dx-dark-mode") !== "false";
    } catch {
      return true;
    }
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [showNotifDropdown, setShowNotifDropdown] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [tableSort, setTableSort] = useState({ key: "amazina", dir: "asc" });
  const [tablePage, setTablePage] = useState(1);
  const [tableFilter, setTableFilter] = useState("");
  const [openQuickGroup, setOpenQuickGroup] = useState(null);
  const tablePerPage = 8;
  const notifRef = useRef(null);
  const profileRef = useRef(null);

  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!sidebarOpen) return;
    const handler = (e) => {
      if (sidebarRef.current && !sidebarRef.current.contains(e.target)) setSidebarOpen(false);
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("touchstart", handler);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("touchstart", handler);
    };
  }, [sidebarOpen]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) setShowNotifDropdown(false);
      if (profileRef.current && !profileRef.current.contains(e.target)) setShowProfileDropdown(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Persist dark mode
  useEffect(() => {
    try {
      localStorage.setItem("dx-dark-mode", darkMode);
    } catch {}
  }, [darkMode]);

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    try {
      const [abashRes, abanaRes, urubRes] = await Promise.all([axios.get(`${API}/abasheshe_akanguhe`), axios.get(`${API}/abana`), axios.get(`${API}/urubyiruko`)]);
      setAbasheshe(abashRes.data || []);
      setAbana(abanaRes.data || []);
      setUrubyiruko(urubRes.data || []);

      try {
        const [abantuRes, ingoRes, myInfoRes] = await Promise.all([
          axios.get(`${API}/umuntu_umwirondoro`),
          axios.get(`${API}/urugo_umuryango`),
          axios.get(`${API}/my-info`),
        ]);
        setAbantu(abantuRes.data || []);
        setIngo(ingoRes.data || []);
        if (myInfoRes.data?.fullname) setUserName(myInfoRes.data.fullname);
      } catch {}

      showToast({ type: 'success', title: 'Murakaza neza!', message: 'Dashboard yagezweho.' });
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("token");
        navigate("/");
      } else {
        showToast({ type: 'danger', title: 'Ikosa', message: 'Habaye ikosa mu gusoma amakuru.' });
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  useEffect(() => {
    if (toast) {
      const t = setTimeout(() => setToast(null), 4500);
      return () => clearTimeout(t);
    }
  }, [toast]);

  const confirmLogout = async () => {
    const ok = await confirmLogoutDialog();
    if (ok) {
      localStorage.clear();
      navigate("/", { replace: true });
    }
  };

  const greeting = () => {
    const h = time.getHours();
    if (h < 12) return "Mwaramutse";
    if (h < 18) return "Mwiriwe";
    return "Mwiriwe";
  };

  const totalPeople = abantu.length + abana.length + urubyiruko.length + abasheshe.length;
  // `activeTab` is either a leaf item's key (a real page) or a group's own
  // id (that group's ModuleOverviewPage) — handle both so the "Aho ugeze"
  // panel, topbar title, and sidebar highlight all stay correct either way.
  const activeGroupDirect = findGroupById(activeTab);
  const activeLeafItem = navItems.find((item) => item.key === activeTab);
  const activeNavItem =
    activeLeafItem ||
    (activeGroupDirect
      ? { key: activeGroupDirect.id, icon: activeGroupDirect.icon, color: activeGroupDirect.color, label: activeGroupDirect.label }
      : navItems[0]);
  const activeSection = activeGroupDirect || findGroupById(findGroupIdForKey(activeTab));

  const dashStats = [
    { label: "Abantu Bose", value: totalPeople, icon: "people-fill", color: "#0047AB", bg: "rgba(0,71,171,0.1)" },
    { label: "Ingo", value: ingo.length, icon: "house-door-fill", color: "#20603D", bg: "rgba(32,96,61,0.1)" },
    { label: "Abana", value: abana.length, icon: "emoji-smile-fill", color: "#e07b00", bg: "rgba(224,123,0,0.1)" },
  ];

  // Quick Actions now mirrors the sidebar's own navGroups (see top of file)
  // instead of a separate hardcoded flat list, so the two can't drift apart.

  // Chart data from real API data
  const gaboCount =
    urubyiruko.filter((u) => (u.igitsina || "").toLowerCase() === "gabo").length +
    abana.filter((a) => (a.igitsina || "").toLowerCase() === "gabo").length +
    abantu.filter((a) => (a.igitsina || "").toLowerCase() === "gabo").length;
  const goreCount =
    urubyiruko.filter((u) => (u.igitsina || "").toLowerCase() === "gore").length +
    abana.filter((a) => (a.igitsina || "").toLowerCase() === "gore").length +
    abantu.filter((a) => (a.igitsina || "").toLowerCase() === "gore").length;

  const genderPieData = [
    { label: "Gabo", value: gaboCount, color: "#0047AB" },
    { label: "Gore", value: goreCount, color: "#c0392b" },
  ];

  const rufiteAkazi = urubyiruko.filter((u) => (u.status || "").toLowerCase().includes("rufite")).length;
  const rudafiteAkazi = urubyiruko.filter((u) => (u.status || "").toLowerCase().includes("rudafite")).length;
  const akaziPieData = [
    { label: "Rufite akazi", value: rufiteAkazi, color: "#20603D" },
    { label: "Rudafite akazi", value: rudafiteAkazi, color: "#e07b00" },
  ];

  const bakenera = abasheshe.filter((a) => (a.ukeneye_ubufasha || "").toLowerCase() === "yego").length;
  const ntibakeneye = abasheshe.filter((a) => (a.ukeneye_ubufasha || "").toLowerCase() === "oya").length;
  const ubufashaPieData = [
    { label: "Bakenera ubufasha", value: bakenera, color: "#c0392b" },
    { label: "Ntibakeneye", value: ntibakeneye, color: "#20603D" },
  ];

  // Group by age brackets for bar chart
  const ageBrackets = useMemo(() => {
    const brackets = ["0-17", "18-30", "31-45", "46-60", "61+"];
    const counts = [0, 0, 0, 0, 0];
    const calcAge = (dob) => {
      if (!dob) return null;
      const b = new Date(dob);
      if (isNaN(b)) return null;
      return Math.floor((Date.now() - b.getTime()) / (365.25 * 24 * 3600 * 1000));
    };
    [...urubyiruko, ...abana, ...abantu, ...abasheshe].forEach((p) => {
      const age = calcAge(p.amatariki_yavukiwe || p.itariki_yavukiwe || p.dateOfBirth);
      if (age === null) return;
      if (age <= 17) counts[0]++;
      else if (age <= 30) counts[1]++;
      else if (age <= 45) counts[2]++;
      else if (age <= 60) counts[3]++;
      else counts[4]++;
    });
    return { labels: brackets, data: counts };
  }, [urubyiruko, abana, abantu, abasheshe]);

  // Category distribution for line chart
  const categoryLineData = useMemo(() => {
    return {
      labels: ["Abana", "Urubyiruko", "Abageze mu Izabukuru", "Abaturage", "Ingo"],
      data: [abana.length, urubyiruko.length, abasheshe.length, abantu.length, ingo.length],
    };
  }, [abana, urubyiruko, abasheshe, abantu, ingo]);

  // Data table — all people combined. Urubyiruko/Abageze mu Zabukuru are
  // intentionally left out here: they're no longer part of the Isibo
  // citizen-management UI (their pages/data still exist, just not
  // surfaced as a navigable category from this dashboard).
  const allPeople = useMemo(() => {
    return [
      ...abana.map((p) => ({ ...p, amazina: p.amazina || p.izina_rya_mbere || "N/A", type: "Abana" })),
      ...abantu.map((p) => ({ ...p, amazina: p.amazina || `${p.izina_rya_mbere || ""} ${p.izina_rya_maka || ""}`.trim() || "N/A", type: "Abaturage" })),
    ];
  }, [abana, abantu]);

  const filteredTable = useMemo(() => {
    let items = [...allPeople];
    if (tableFilter) {
      const q = tableFilter.toLowerCase();
      items = items.filter((p) => (p.amazina || "").toLowerCase().includes(q) || (p.type || "").toLowerCase().includes(q) || (p.igitsina || "").toLowerCase().includes(q));
    }
    items.sort((a, b) => {
      let va = a[tableSort.key] || "",
        vb = b[tableSort.key] || "";
      if (typeof va === "number" && typeof vb === "number") return tableSort.dir === "asc" ? va - vb : vb - va;
      va = String(va).toLowerCase();
      vb = String(vb).toLowerCase();
      if (va < vb) return tableSort.dir === "asc" ? -1 : 1;
      if (va > vb) return tableSort.dir === "asc" ? 1 : -1;
      return 0;
    });
    return items;
  }, [allPeople, tableFilter, tableSort]);

  const totalPages = Math.max(1, Math.ceil(filteredTable.length / tablePerPage));
  const paginatedTable = filteredTable.slice((tablePage - 1) * tablePerPage, tablePage * tablePerPage);

  // Clamp page on filter change
  useEffect(() => {
    setTablePage(1);
  }, [tableFilter]);

  const handleSort = (key) => {
    setTableSort((prev) => (prev.key === key ? { key, dir: prev.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));
  };

  // Export CSV
  const handleExportCSV = () => {
    const rows = [["Amazina", "Ubwoko", "Igitsina", "Status"]];
    allPeople.forEach((p) => {
      rows.push([p.amazina, p.type, p.igitsina || "", p.status || ""]);
    });
    const csv = rows.map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "isibo_data_export.csv";
    a.click();
    URL.revokeObjectURL(url);
      showToast({ type: 'success', title: 'Excel yakurwemo neza!', message: 'Dosiye yagurumye.' });
  };

  // Notifications (simulated recent activity)
  const notifications = useMemo(() => {
    const items = [];
    if (allPeople.length > 0) {
      const last = allPeople[allPeople.length - 1];
      items.push({ id: 1, desc: `${last.amazina} yongewemo muri ${last.type}`, time: "Vuba", color: "#20603D" });
    }
    if (ingo.length > 0) {
      items.push({ id: 2, desc: `Ingo ${ingo.length} ziri muri sisitemu`, time: "Uyu munsi", color: "#0047AB" });
    }
    if (bakenera > 0) {
      items.push({ id: 3, desc: `Abageze mu Izabukuru ${bakenera} bakenera ubufasha`, time: "Uyu munsi", color: "#c0392b" });
    }
    items.push({ id: 4, desc: "Dashboard yavuguruwe", time: "Nonaha", color: "#0047AB" });
    return items;
  }, [allPeople, ingo, bakenera]);

   if (loading) {
     return (
       <>
         <DashStyles />
         <div className="dx-root">
           <div className="dx-loader-screen">
             <div className="dx-loader-ring"></div>
             <div className="dx-loader-text">Gutegereza...</div>
           </div>
         </div>
       </>
     );
   }

  return (
    <>
      <DashStyles />
      {/* Dark mode class on root */}
      <div className={`dx-root ${darkMode ? "dx-dark" : "dx-light"}`}>
        <div className="dx-shell">
          {/* SIDEBAR (Desktop) */}
          <aside className={`dx-sidebar ${sidebarOpen ? "open" : ""}`} ref={sidebarRef}>
            <div className="dx-sidebar-head">
              <div className="dx-logo">
                <div className="dx-logo-icon">
                  <i className="bi bi-grid-fill"></i>
                </div>
                <div className="dx-logo-text">
                  <span className="dx-logo-title">Isibo</span>
                  <span className="dx-logo-sub">Dashboard</span>
                </div>
              </div>
              <button className="dx-sidebar-close d-lg-none" onClick={() => setSidebarOpen(false)}>
                <i className="bi bi-x-lg"></i>
              </button>
            </div>

            <div className="dx-sidebar-panel">
              <div className="dx-sidebar-panel-top">
                <span className="dx-sidebar-panel-kicker">Aho ugeze</span>
                <span className="dx-sidebar-panel-chip">{activeSection?.label || "Menu"}</span>
              </div>
              <div className="dx-sidebar-panel-main">
                <div className="dx-sidebar-panel-icon" style={{ background: `${activeNavItem.color}22`, color: activeNavItem.color }}>
                  <i className={getIconClassName(activeNavItem.icon)}></i>
                </div>
                <div className="dx-sidebar-panel-copy">
                  <strong>{activeNavItem.label}</strong>
                  <span>{time.toLocaleTimeString("rw-RW", { hour: "2-digit", minute: "2-digit" })}</span>
                </div>
              </div>
            </div>

            <nav className="dx-nav">
              <div className="dx-nav-head">
                <div>
                  <span className="dx-nav-head-title">Navigation</span>
                  <span className="dx-nav-head-sub">{sidebarGroups.length} ibice</span>
                </div>
                <span className="dx-nav-head-chip">Live</span>
              </div>

              {/* Sidebar = main modules only. Clicking one either goes straight
                  to its page (single-item groups, e.g. Dashboard) or to that
                  module's overview page, which lists its own sub-features as
                  cards — see <ModuleOverviewPage> below. */}
              <div className="dx-nav-stack">
                {sidebarGroups.map((group) => {
                  const destination = group.items.length === 1 ? group.items[0].key : group.id;
                  const isActive = sidebarAncestorId(findGroupIdForKey(activeTab) || activeTab) === group.id;
                  return (
                    <button
                      key={group.id}
                      className={`dx-nav-item ${isActive ? "active" : ""}`}
                      style={{ "--dx-item-color": group.color, "--dx-item-tint": hexToRgba(group.color, 0.16) }}
                      onClick={() => {
                        setActiveTab(destination);
                        setSidebarOpen(false);
                      }}
                    >
                      <div className="dx-nav-icon">
                        <i className={getIconClassName(group.icon)}></i>
                      </div>
                      <span className="dx-nav-label-text">{group.label}</span>
                      {isActive && <div className="dx-nav-indicator"></div>}
                    </button>
                  );
                })}
              </div>
            </nav>

            <div className="dx-sidebar-foot">
              {/* "Konti" (Account) — pinned outside the scrollable nav so
                  Sohoka stays reachable without scrolling, per spec. */}
              <div className="dx-nav-label dx-nav-label-foot">Konti</div>
              <button className="dx-logout-btn" onClick={confirmLogout}>
                <i className="bi bi-box-arrow-left"></i>
                <span>Sohoka</span>
              </button>
            </div>
          </aside>

          {/* SIDEBAR OVERLAY (Mobile) */}
          {sidebarOpen && <div className="dx-overlay d-lg-none" onClick={() => setSidebarOpen(false)}></div>}

          {/* MAIN AREA */}
          <main className="dx-main">
            {/* Top Bar */}
            <header className="dx-topbar">
              <button className="dx-hamburger d-lg-none" onClick={() => setSidebarOpen(true)}>
                <i className="bi bi-list"></i>
              </button>

              <div className="dx-topbar-info">
                <div className="dx-topbar-badge" style={{ "--dx-active-color": activeNavItem.color }}>
                  <i className={getIconClassName(activeNavItem.icon)}></i>
                  <span>{activeSection?.label || "Navigation"}</span>
                </div>
                <h1 className="dx-page-title">{activeNavItem.label || "Dashboard"}</h1>
                <p className="dx-page-sub">
                  {activeTab === "dashboard" ? `${greeting()} 👋 — ${time.toLocaleDateString("rw-RW", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}` : "Koresha menu yo ku ruhande"}
                </p>
              </div>

              <div className="dx-topbar-actions">
                {/* Search bar in topbar */}
                <div className="dx-topbar-search">
                  <i className="bi bi-search"></i>
                  <input type="text" placeholder="Shakisha..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
                </div>

                {/* Dark mode toggle */}
                <button className="dx-icon-btn" onClick={() => setDarkMode(!darkMode)} title={darkMode ? "Light mode" : "Dark mode"}>
                  <i className={`bi bi-${darkMode ? "sun-fill" : "moon-fill"}`}></i>
                </button>

                {/* Export button */}
                {activeTab === "dashboard" && (
                  <button className="dx-icon-btn dx-export-icon" onClick={handleExportCSV} title="Kuramo Excel">
                    <i className="bi bi-download"></i>
                  </button>
                )}

                {/* Notifications dropdown */}
                <div ref={notifRef} className="dx-notif-wrapper">
                  <button
                    className="dx-icon-btn"
                    onClick={() => {
                      setShowNotifDropdown(!showNotifDropdown);
                      setShowProfileDropdown(false);
                    }}
                    title="Amatangazo"
                  >
                    <i className="bi bi-bell"></i>
                    {notifications.length > 0 && <span className="dx-notif-badge">{notifications.length}</span>}
                  </button>
                  {showNotifDropdown && (
                    <div className="dx-dropdown">
                      <div className="dx-dropdown-head">
                        <span>Amatangazo</span>
                        <span className="dx-dropdown-count">{notifications.length}</span>
                      </div>
                      {notifications.map((n) => (
                        <div key={n.id} className="dx-dropdown-item">
                          <span className="dx-dropdown-dot" style={{ background: n.color }}></span>
                          <div className="dx-dropdown-item-body">
                            <span className="dx-dropdown-item-text">{n.desc}</span>
                            <span className="dx-dropdown-item-time">{n.time}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Profile dropdown */}
                <div ref={profileRef} className="dx-notif-wrapper">
                  <button
                    className="dx-profile-btn"
                    onClick={() => {
                      setShowProfileDropdown(!showProfileDropdown);
                      setShowNotifDropdown(false);
                    }}
                  >
                    <div className="dx-avatar">{(userName || "IS").substring(0, 2).toUpperCase()}</div>
                    <span className="dx-profile-name d-none d-md-inline">{userName || "Isibo"}</span>
                    <i className="bi bi-chevron-down dx-profile-chevron"></i>
                  </button>
                  {showProfileDropdown && (
                    <div className="dx-dropdown">
                      <div className="dx-dropdown-head">
                        <span>{userName || "Isibo"}</span>
                      </div>
                      <div className="dx-dropdown-item">
                        <i className="bi bi-person"></i>
                        <span>Umwirondoro</span>
                      </div>
                      <div className="dx-dropdown-item">
                        <i className="bi bi-gear"></i>
                        <span>Igenamiterere</span>
                      </div>
                      <div className="dx-dropdown-divider"></div>
                      <div className="dx-dropdown-item dx-dropdown-danger" onClick={confirmLogout}>
                        <i className="bi bi-box-arrow-right"></i>
                        <span>Sohoka</span>
                      </div>
                    </div>
                  )}
                </div>

                <button className="dx-icon-btn" onClick={fetchAllData} title="Ongera">
                  <i className="bi bi-arrow-clockwise"></i>
                </button>
              </div>
            </header>

            {/* Content */}
            <div className="dx-content">
              {/* Dashboard Tab */}
              {activeTab === "dashboard" && (
                <div className="dx-dashboard" style={{ animation: "dxFadeUp 0.5s ease" }}>
                  {/* Stats Grid */}
                  <div className="dx-stats-grid">
                    {dashStats.map((s, i) => (
                      <div key={i} className="dx-stat-card" style={{ animationDelay: `${i * 0.06}s` }}>
                        <div className="dx-stat-icon" style={{ background: s.bg, color: s.color }}>
                          <i className={`bi bi-${s.icon}`}></i>
                        </div>
                        <div className="dx-stat-info">
                          <div className="dx-stat-value"><AnimatedStatValue value={s.value} /></div>
                          <div className="dx-stat-label">{s.label}</div>
                        </div>
                        <div className="dx-stat-glow" style={{ background: s.color }}></div>
                      </div>
                    ))}
                  </div>

                  {/* Charts Row 1 — Line + Bar */}
                  <div className="dx-charts-row">
                    <div className="dx-chart-card">
                      <div className="dx-chart-card-head">
                        <h3 className="dx-chart-title">
                          <i className="bi bi-bar-chart-line" style={{ color: "#475569" }}></i>
                          Abaturage mu Bwoko
                        </h3>
                        <span className="dx-chart-badge">Real data</span>
                      </div>
                      <SvgLineChart data={categoryLineData.data} labels={categoryLineData.labels} color="#0047AB" height={220} />
                    </div>
                    <div className="dx-chart-card">
                      <div className="dx-chart-card-head">
                        <h3 className="dx-chart-title">
                          <i className="bi bi-bar-chart" style={{ color: "#20603D" }}></i>
                          Ikigereranyo cy'Imyaka
                        </h3>
                        <span className="dx-chart-badge">Real data</span>
                      </div>
                      <SvgBarChart data={ageBrackets.data} labels={ageBrackets.labels} color="#20603D" height={220} />
                    </div>
                  </div>

                  {/* Charts Row 2 — Pie Charts */}
                  <div className="dx-charts-row">
                    <div className="dx-chart-card">
                      <div className="dx-chart-card-head">
                        <h3 className="dx-chart-title">
                          <i className="bi bi-gender-ambiguous" style={{ color: "#c0392b" }}></i>
                          Igitsina
                        </h3>
                        <span className="dx-chart-badge">Real data</span>
                      </div>
                      <div className="dx-chart-body">
                        <SvgPieChart data={genderPieData} size={200} />
                      </div>
                    </div>
                    <div className="dx-chart-card">
                      <div className="dx-chart-card-head">
                        <h3 className="dx-chart-title">
                          <i className="bi bi-briefcase" style={{ color: "#20603D" }}></i>
                          Akazi n'Ubufasha
                        </h3>
                        <span className="dx-chart-badge">Real data</span>
                      </div>
                      <div className="dx-chart-body">
                        <div className="dx-pie-row">
                          <div className="dx-pie-mini">
                            <div className="dx-pie-mini-label">Urubyiruko & Akazi</div>
                            <SvgPieChart data={akaziPieData} size={160} />
                          </div>
                          <div className="dx-pie-mini">
                            <div className="dx-pie-mini-label">Abageze mu Izabukuru & Ubufasha</div>
                            <SvgPieChart data={ubufashaPieData} size={160} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Quick Actions */}
                  <div className="dx-section">
                    <div className="dx-section-head">
                      <h2 className="dx-section-title">
                        <i className="bi bi-lightning-charge-fill" style={{ color: "#FAD201", marginRight: 8 }}></i>
                        Ibikorwa byihuse
                      </h2>
                    </div>
                    <div className="dx-qa-groups">
                      {sidebarGroups.map((group) => {
                        const isOpen = openQuickGroup === group.id;
                        return (
                          <div key={group.id} className="dx-qa-group">
                            <button
                              type="button"
                              className="dx-qa-group-head"
                              aria-expanded={isOpen}
                              onClick={() => setOpenQuickGroup((k) => (k === group.id ? null : group.id))}
                            >
                              <div className="dx-qa-group-icon" style={{ background: `${group.color}18`, color: group.color }}>
                                <i className={getIconClassName(group.icon)}></i>
                              </div>
                              <span className="dx-qa-group-label">{group.label}</span>
                              <span className="dx-qa-group-count">{group.items.length}</span>
                              <i className={`bi bi-chevron-${isOpen ? "up" : "down"} dx-qa-group-chevron`}></i>
                            </button>
                            {isOpen && (
                              <div className="dx-quick-grid">
                                {group.items.map((item, i) => (
                                  <button
                                    key={item.key}
                                    className="dx-quick-btn"
                                    onClick={() => setActiveTab(item.key)}
                                    style={{ animationDelay: `${i * 0.06}s` }}
                                  >
                                    <div className="dx-quick-icon" style={{ background: `${item.color}18`, color: item.color }}>
                                      <i className={getIconClassName(item.icon)}></i>
                                    </div>
                                    <span>{item.label}</span>
                                    <i className="bi bi-chevron-right dx-quick-arrow"></i>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Data Table with sorting, filtering, pagination */}
                  <div className="dx-section">
                    <div className="dx-section-head">
                      <h2 className="dx-section-title">
                        <i className="bi bi-table" style={{ color: "#475569", marginRight: 8 }}></i>
                        Lisiti y'Abaturage
                      </h2>
                      <button className="dx-export-table-btn" onClick={handleExportCSV}>
                        <i className="bi bi-download"></i>
                        Kuramo Excel
                      </button>
                    </div>
                    <div className="dx-table-wrap">
                      <div className="dx-table-controls">
                        <div className="dx-table-search">
                          <i className="bi bi-search"></i>
                          <input type="text" placeholder="Shakisha umuntu..." value={tableFilter} onChange={(e) => setTableFilter(e.target.value)} />
                        </div>
                        <span className="dx-table-count">{filteredTable.length} abantu</span>
                      </div>
                      <div className="dx-table-scroll">
                        <table className="dx-table">
                          <thead>
                            <tr>
                              <th onClick={() => handleSort("amazina")} className="dx-th-sortable">
                                <span>Amazina</span>
                                {tableSort.key === "amazina" && <i className={`bi bi-arrow-${tableSort.dir === "asc" ? "up" : "down"}`}></i>}
                              </th>
                              <th onClick={() => handleSort("type")} className="dx-th-sortable">
                                <span>Ubwoko</span>
                                {tableSort.key === "type" && <i className={`bi bi-arrow-${tableSort.dir === "asc" ? "up" : "down"}`}></i>}
                              </th>
                              <th onClick={() => handleSort("igitsina")} className="dx-th-sortable">
                                <span>Igitsina</span>
                                {tableSort.key === "igitsina" && <i className={`bi bi-arrow-${tableSort.dir === "asc" ? "up" : "down"}`}></i>}
                              </th>
                              <th>Ibikorwa</th>
                            </tr>
                          </thead>
                          <tbody>
                            {paginatedTable.length === 0 ? (
                              <tr>
                                <td colSpan={4} className="dx-table-empty">
                                  Nta muntu yabonetse
                                </td>
                              </tr>
                            ) : (
                              paginatedTable.map((p, i) => (
                                <tr key={i}>
                                  <td>
                                    <div className="dx-table-user">
                                      <div
                                        className="dx-table-avatar"
                                        style={{
                                          background: p.type === "Urubyiruko" ? "rgba(0,71,171,0.12)" : p.type === "Abana" ? "rgba(224,123,0,0.12)" : p.type === "Abageze mu Izabukuru" ? "rgba(192,57,43,0.12)" : "rgba(32,96,61,0.12)",
                                          color: p.type === "Urubyiruko" ? "#0047AB" : p.type === "Abana" ? "#e07b00" : p.type === "Abageze mu Izabukuru" ? "#c0392b" : "#20603D",
                                        }}
                                      >
                                        {(p.amazina || "N").charAt(0).toUpperCase()}
                                      </div>
                                      <span>{p.amazina}</span>
                                    </div>
                                  </td>
                                  <td>
                                    <span className="dx-table-type">{p.type}</span>
                                  </td>
                                  <td>
                                    <span className="dx-table-gender">{p.igitsina || "—"}</span>
                                  </td>
                                  <td>
                                    <div className="dx-table-actions">
                                      <button
                                        className="dx-table-action"
                                        title="Reba"
                                        onClick={() => setActiveTab(p.type === "Abana" ? "abana" : "abaturage")}
                                      >
                                        <i className="bi bi-eye"></i>
                                      </button>
                                      <button
                                        className="dx-table-action"
                                        title="Hindura"
                                        onClick={() => setActiveTab(p.type === "Abana" ? "abana" : "abaturage")}
                                      >
                                        <i className="bi bi-pencil"></i>
                                      </button>
                                      <button className="dx-table-action dx-table-action-danger" title="Siba">
                                        <i className="bi bi-trash3"></i>
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                      {/* Pagination */}
                      {totalPages > 1 && (
                        <div className="dx-pagination">
                          <button className="dx-page-btn" disabled={tablePage <= 1} onClick={() => setTablePage((p) => p - 1)}>
                            <i className="bi bi-chevron-left"></i>
                          </button>
                          {Array.from({ length: totalPages }, (_, i) => i + 1).map((pg) => (
                            <button key={pg} className={`dx-page-btn ${tablePage === pg ? "dx-page-active" : ""}`} onClick={() => setTablePage(pg)}>
                              {pg}
                            </button>
                          ))}
                          <button className="dx-page-btn" disabled={tablePage >= totalPages} onClick={() => setTablePage((p) => p + 1)}>
                            <i className="bi bi-chevron-right"></i>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Activity Feed + Notifications */}
                  <div className="dx-bottom-row">
                    {/* Activity Feed */}
                    <div className="dx-panel">
                      <div className="dx-panel-head">
                        <h3 className="dx-panel-title">
                          <i className="bi bi-activity" style={{ color: "#475569" }}></i>
                          Ibikorwa vuba
                        </h3>
                      </div>
                      <div className="dx-activity-list">
                        {notifications.map((n) => (
                          <div key={n.id} className="dx-activity-item">
                            <div className="dx-activity-dot" style={{ background: n.color }}></div>
                            <div className="dx-activity-body">
                              <div className="dx-activity-text">{n.desc}</div>
                              <div className="dx-activity-time">
                                <i className="bi bi-clock"></i> {n.time}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Notifications Panel */}
                    <div className="dx-panel">
                      <div className="dx-panel-head">
                        <h3 className="dx-panel-title">
                          <i className="bi bi-bell" style={{ color: "#e07b00" }}></i>
                          Amatangazo
                        </h3>
                      </div>
                      <div className="dx-notif-panel-list">
                        {notifications.map((n) => (
                          <div key={n.id} className="dx-notif-panel-item">
                            <div className="dx-notif-panel-icon" style={{ background: `${n.color}15`, color: n.color }}>
                              <i className="bi bi-check-circle-fill"></i>
                            </div>
                            <div className="dx-notif-panel-body">
                              <div className="dx-notif-panel-title">{n.desc}</div>
                              <div className="dx-notif-panel-time">{n.time}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Module overview pages — Level 2 of the nav. Reached by
                  clicking a multi-item sidebar module; shows that module's
                  items as cards. Single-item groups (Dashboard, Amatangazo,
                  Ubutumwa) skip straight to their one page instead — see the
                  sidebar's `destination` logic above. */}
              {navGroups
                .filter((group) => group.items.length > 1 && activeTab === group.id)
                .map((group) => (
                  <ModuleOverviewPage
                    key={group.id}
                    icon={group.icon}
                    color={group.color}
                    label={group.label}
                    description={group.description}
                    items={group.items}
                    onNavigate={setActiveTab}
                  />
                ))}

              {/* Other Tabs */}
              {activeTab === "umuntu-umwirondoro" && (
                <div style={{ animation: "dxFadeUp 0.4s ease" }}>
                  <UmuntuUmwirondoro />
                </div>
              )}
              {activeTab === "urugo-umuryango" && (
                <div style={{ animation: "dxFadeUp 0.4s ease" }}>
                  <UrugoUmuryango />
                </div>
              )}
              {activeTab === "umuntu-mu-rugo" && (
                <div style={{ animation: "dxFadeUp 0.4s ease" }}>
                  <UmuntuMuRugo />
                </div>
              )}
              {activeTab === "urubyiruko" && (
                <div style={{ animation: "dxFadeUp 0.4s ease" }}>
                  <Urubyiruko />
                </div>
              )}
              {activeTab === "abana" && (
                <div style={{ animation: "dxFadeUp 0.4s ease" }}>
                  <Abana />
                </div>
              )}
              {activeTab === "abasheshe" && (
                <div style={{ animation: "dxFadeUp 0.4s ease" }}>
                  <AbashesheAkanguhe />
                </div>
              )}
              {activeTab === "amacyimbirane" && (
                <div style={{ animation: "dxFadeUp 0.4s ease" }}>
                  <IngoZifiteAmacyimbirane />
                </div>
              )}
              {activeTab === "imibereho" && (
                <div style={{ animation: "dxFadeUp 0.4s ease" }}>
                  <ImiberehoMyiza />
                </div>
              )}
              {activeTab === "ingo" && (
                <div style={{ animation: "dxFadeUp 0.4s ease" }}>
                  <Ingo />
                </div>
              )}
              {activeTab === "ubwishingizi" && (
                <div style={{ animation: "dxFadeUp 0.4s ease" }}>
                  <Ubwishingizi />
                </div>
              )}
              {activeTab === "ubukungu" && (
                <div style={{ animation: "dxFadeUp 0.4s ease" }}>
                  <Ubukungu />
                </div>
              )}
              {activeTab === "uburezi" && (
                <div style={{ animation: "dxFadeUp 0.4s ease" }}>
                  <Uburezi />
                </div>
              )}
              {activeTab === "ubuzima" && (
                <div style={{ animation: "dxFadeUp 0.4s ease" }}>
                  <Ubuzima />
                </div>
              )}
              {activeTab === "abaturage" && (
                <div style={{ animation: "dxFadeUp 0.4s ease" }}>
                  <Abaturage />
                </div>
              )}
              {activeTab === "inkunga" && (
                <div style={{ animation: "dxFadeUp 0.4s ease" }}>
                  <AbaturageBafiteInkunguzaleta />
                </div>
              )}
              {activeTab === "umusanzuEjoheza" && (
                <div style={{ animation: "dxFadeUp 0.4s ease" }}>
                  <UmusanzuEjoheza />
                </div>
              )}
              {activeTab === "gutangaUmutekano" && (
                <div style={{ animation: "dxFadeUp 0.4s ease" }}>
                  <GutangaUmutekano />
                </div>
              )}
              {activeTab === "kurwanyaUmwanda" && (
                <div style={{ animation: "dxFadeUp 0.4s ease" }}>
                  <KurwanyaUmwanda />
                </div>
              )}
              {activeTab === "isukuIsukura" && (
                <div style={{ animation: "dxFadeUp 0.4s ease" }}>
                  <IsukuIsukura />
                </div>
              )}

              {activeTab === "imibare" && (
                <div style={{ animation: "dxFadeUp 0.4s ease" }}>
                  <IsiboStatistics />
                  <ImibareNavigation />
                </div>
              )}
              {activeTab === "ibiza" && (
                <div style={{ animation: "dxFadeUp 0.4s ease" }}>
                  <Ibiza />
                </div>
              )}
              {activeTab === "attendance-mgmt" && (
                <div style={{ animation: "dxFadeUp 0.4s ease" }}>
                  <IsiboAttendance />
                </div>
              )}


              {activeTab === "messaging" && (
                <div style={{ animation: "dxFadeUp 0.4s ease" }}>
                  <Messaging />
                </div>
              )}
              {activeTab === "amatangazo" && (
                <div style={{ animation: "dxFadeUp 0.4s ease" }}>
                  <AmatangazoIsibo />
                </div>
              )}
              {activeTab === "raporo" && (
                <div style={{ animation: "dxFadeUp 0.4s ease" }}>
                  <ReportPage />
                </div>
              )}
              {activeTab === "raporo-zanjye" && (
                <div style={{ animation: "dxFadeUp 0.4s ease" }}>
                  <IbikorwaRaporo />
                </div>
              )}
              {activeTab === "abashyitsi" && (
                <div style={{ animation: "dxFadeUp 0.4s ease" }}>
                  <AbashyitsiIsibo />
                </div>
              )}
              {activeTab === "bariza-umuturage" && (
                <div style={{ animation: "dxFadeUp 0.4s ease" }}>
                  <BarizaUmuturage />
                </div>
              )}

              {activeTab === "ibikorwa-remezo" && (
                <div style={{ animation: "dxFadeUp 0.4s ease" }}>
                  <IbikorwaRemezoForm />
                </div>
              )}

              {activeTab === "ibirego-kwandika" && (
                <div style={{ animation: "dxFadeUp 0.4s ease" }}>
                  <div style={{ padding: "0 0 20px" }}>
                    <IbiregoForm />
                  </div>
                </div>
              )}

              {activeTab === "imico-imyifatire" && (
                <div style={{ animation: "dxFadeUp 0.4s ease" }}>
                  <ImicoImyifatire mode="creator" />
                </div>
              )}

              {activeTab === "abana-mu-mihanda" && (
                <div style={{ animation: "dxFadeUp 0.4s ease" }}>
                  <AbanaMuMihanda mode="creator" />
                </div>
              )}

            </div>
          </main>

          {/* MOBILE BOTTOM NAV */}
          <nav className="dx-bottom-nav d-lg-none">
            {bottomNavItems.map((item) => (
              <button
                key={item.key}
                className={`dx-bottom-item ${activeTab === item.key ? "active" : ""}`}
                style={{ "--dx-item-color": item.color }}
                onClick={() => setActiveTab(item.key)}
              >
                <i className={getIconClassName(item.icon)}></i>
                <span>{item.label}</span>
                {activeTab === item.key && <div className="dx-bottom-dot" style={{ background: item.color }}></div>}
              </button>
            ))}
            <button className="dx-bottom-item" onClick={() => setSidebarOpen(true)}>
              <i className="bi bi-three-dots"></i>
              <span>Ibindi</span>
            </button>
          </nav>

           {/* TOAST */}
           {/* Toast is now handled globally by ToastProvider */}
           
           {/* LOGOUT handled by ConfirmDialog system */}
        </div>
      </div>
    </>
  );
}

const DashStyles = () => (
  <style>{`
@import url('https://fonts.googleapis.com/css2?family=Nunito+Sans:wght@300;400;500;600;700;800;900&display=swap');

/* ── RWANDA GOVERNMENT PALETTE ──
   Primary: Rwanda Blue #0047AB
   Accent:  Rwanda Yellow #FAD201
   Success: Rwanda Green #20603D
   Background: clean white/light grey
*/
:root {
  --dx-bg:           #f0f4f8;
  --dx-bg2:          #ffffff;
  --dx-bg3:          #e8edf3;
  --dx-border:       #e2e8f0;
  --dx-border-light: #cbd5e1;
  --dx-text:         #0d1b2a;
  --dx-text-muted:   #4a5568;
  --dx-text-dim:     #718096;
  --dx-primary:      #0047AB;
  --dx-primary-soft: rgba(0,71,171,0.10);
  --dx-accent:       #FAD201;
  --dx-success:      #20603D;
  --dx-warning:      #e07b00;
  --dx-danger:       #c0392b;
  --dx-radius:       10px;
  --dx-radius-sm:    6px;
  --dx-radius-lg:    14px;
  --dx-transition:   all 0.2s cubic-bezier(0.4,0,0.2,1);
  --dx-shadow:       0 2px 8px rgba(0,0,0,0.07);
  --dx-shadow-lg:    0 6px 20px rgba(0,0,0,0.10);
}

.dx-root *, .dx-root *::before, .dx-root *::after {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
  font-family: 'Nunito Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  -webkit-tap-highlight-color: transparent;
}
.dx-root { 
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}
.dx-root ::-webkit-scrollbar { width: 5px; height: 5px; }
.dx-root ::-webkit-scrollbar-track { background: #e8edf3; }
.dx-root ::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
.dx-root ::-webkit-scrollbar-thumb:hover { background: #94a3b8; }

/* ANIMATIONS */
@keyframes dxFadeUp { from { opacity: 0; transform: translateY(24px); } to { opacity: 1; transform: translateY(0); } }
@keyframes dxScale { from { opacity: 0; transform: scale(0.94); } to { opacity: 1; transform: scale(1); } }
@keyframes dxPop {
  0% { opacity: 0; transform: translateY(-10px) scale(0.94); }
  60% { transform: translateY(2px) scale(1.01); }
  100% { opacity: 1; transform: translateY(0) scale(1); }
}
@keyframes dxGlow { 0%,100% { opacity: 0.3; } 50% { opacity: 0.6; } }
@keyframes dxSpin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
@keyframes dxSlideIn { from { transform: translateX(-100%); } to { transform: translateX(0); } }
@keyframes dxPulse { 0%,100% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.05); opacity: 0.8; } }
@keyframes dxCardIn { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
@keyframes dxShimmer {
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}
@keyframes dxFloat {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-8px); }
}
@keyframes dxGradientShift {
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}
@keyframes dxRipple {
  0% { transform: scale(0); opacity: 0.5; }
  100% { transform: scale(4); opacity: 0; }
}

/* MAIN SHELL */
.dx-shell {
  display: flex;
  min-height: 100vh;
  min-height: 100dvh;
  color: var(--dx-text);
  position: relative;
  overflow-x: hidden;
  align-items: flex-start;
}

/* LIGHT (government default) */
.dx-dark .dx-shell,
.dx-light .dx-shell {
  background: #f0f4f8;
}

/* GOVERNMENT SIDEBAR */
.dx-sidebar {
  width: 260px;
  min-width: 260px;
  background: #0047AB;
  border-right: none;
  display: flex;
  flex-direction: column;
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  z-index: 200;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 0;
  gap: 0;
  box-shadow: 3px 0 16px rgba(0,71,171,0.18);
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.dx-light .dx-sidebar,
.dx-dark .dx-sidebar {
  background: #0047AB;
  border-right: none;
}

.dx-sidebar::before { display: none; }
.dx-sidebar::after { display: none; }

/* Sidebar top government banner */
.dx-sidebar-head {
  padding: 0;
  border-radius: 0;
  background: #003380;
  border: none;
  border-bottom: 3px solid #FAD201;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  z-index: 1;
  padding: 16px 18px;
  flex-shrink: 0;
}

.dx-light .dx-sidebar-head,
.dx-dark .dx-sidebar-head {
  background: #003380;
  border: none;
  border-bottom: 3px solid #FAD201;
}

.dx-logo { display: flex; align-items: center; gap: 12px; }
.dx-logo-icon {
  width: 44px; height: 44px; border-radius: 8px;
  background: #FAD201;
  display: flex; align-items: center; justify-content: center;
  font-size: 1.1rem; color: #003380;
  box-shadow: 0 3px 10px rgba(0,0,0,0.2);
  font-weight: 900;
}
.dx-logo-title { font-weight: 800; font-size: 1rem; color: #ffffff; display: block; letter-spacing: 0.01em; }
.dx-light .dx-logo-title, .dx-dark .dx-logo-title { color: #ffffff; }
.dx-logo-sub { font-size: 0.6rem; color: rgba(250,210,1,0.85); font-weight: 600; display: block; letter-spacing: 0.1em; text-transform: uppercase; }
.dx-light .dx-logo-sub, .dx-dark .dx-logo-sub { color: rgba(250,210,1,0.85); }

.dx-sidebar-close {
  width: 32px; height: 32px; border-radius: 6px;
  border: 1px solid rgba(255,255,255,0.2); background: rgba(255,255,255,0.1);
  color: #fff; cursor: pointer; display: flex;
  align-items: center; justify-content: center; font-size: 0.9rem;
  transition: var(--dx-transition);
}
.dx-light .dx-sidebar-close, .dx-dark .dx-sidebar-close { border-color: rgba(255,255,255,0.2); background: rgba(255,255,255,0.1); color: #fff; }
.dx-sidebar-close:hover { background: rgba(255,255,255,0.2); transform: rotate(90deg); }

.dx-sidebar-panel {
  padding: 12px 14px;
  border-radius: 0;
  background: rgba(0,0,0,0.15);
  border: none;
  border-bottom: 1px solid rgba(255,255,255,0.1);
  display: grid;
  gap: 8px;
  position: relative;
  z-index: 1;
  flex-shrink: 0;
  overflow: hidden;
  margin: 0;
}
.dx-light .dx-sidebar-panel, .dx-dark .dx-sidebar-panel {
  background: rgba(0,0,0,0.15);
  border: none;
  border-bottom: 1px solid rgba(255,255,255,0.1);
}

.dx-sidebar-panel-top { display: flex; align-items: center; justify-content: space-between; gap: 10px; }
.dx-sidebar-panel-kicker { font-size: 0.58rem; letter-spacing: 0.12em; text-transform: uppercase; color: rgba(255,255,255,0.55); font-weight: 700; }
.dx-sidebar-panel-chip { padding: 4px 9px; border-radius: 4px; background: rgba(250,210,1,0.15); border: 1px solid rgba(250,210,1,0.3); color: #FAD201; font-size: 0.65rem; font-weight: 700; }
.dx-light .dx-sidebar-panel-chip, .dx-dark .dx-sidebar-panel-chip { background: rgba(250,210,1,0.15); border-color: rgba(250,210,1,0.3); color: #FAD201; }
.dx-sidebar-panel-main { display: flex; align-items: center; gap: 10px; }
.dx-sidebar-panel-icon {
  width: 38px; height: 38px; min-width: 38px; border-radius: 7px;
  display: flex; align-items: center; justify-content: center;
  font-size: 0.95rem; border: 1px solid rgba(255,255,255,0.15);
}
.dx-light .dx-sidebar-panel-icon, .dx-dark .dx-sidebar-panel-icon { border-color: rgba(255,255,255,0.15); }
.dx-sidebar-panel-copy { min-width: 0; display: grid; gap: 2px; }
.dx-sidebar-panel-copy strong { font-size: 0.85rem; font-weight: 700; color: #ffffff; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.dx-light .dx-sidebar-panel-copy strong, .dx-dark .dx-sidebar-panel-copy strong { color: #ffffff; }
.dx-sidebar-panel-copy span { color: rgba(255,255,255,0.5); font-size: 0.68rem; }

.dx-nav { flex: 1; min-height: 0; padding: 10px 12px; overflow-y: auto; position: relative; z-index: 1; }
.dx-nav-head { display: flex; align-items: center; justify-content: space-between; gap: 10px; margin-bottom: 10px; padding: 0 4px; }
.dx-nav-head-title { display: block; font-size: 0.8rem; font-weight: 700; color: #ffffff; letter-spacing: 0.01em; }
.dx-light .dx-nav-head-title, .dx-dark .dx-nav-head-title { color: #ffffff; }
.dx-nav-head-sub { display: block; font-size: 0.6rem; color: rgba(255,255,255,0.45); margin-top: 2px; }
.dx-nav-head-chip { padding: 4px 9px; border-radius: 4px; font-size: 0.6rem; font-weight: 700; color: #FAD201; background: rgba(250,210,1,0.12); border: 1px solid rgba(250,210,1,0.25); }
.dx-light .dx-nav-head-chip, .dx-dark .dx-nav-head-chip { color: #FAD201; background: rgba(250,210,1,0.12); border-color: rgba(250,210,1,0.25); }

.dx-nav-group { padding: 4px; border-radius: 6px; border: none; background: transparent; margin-bottom: 4px; }
.dx-light .dx-nav-group, .dx-dark .dx-nav-group { border: none; background: transparent; }
.dx-nav-group + .dx-nav-group { margin-top: 10px; }

.dx-nav-label { font-size: 0.78rem; font-weight: 800; color: rgba(255,255,255,0.85); letter-spacing: 0.08em; text-transform: uppercase; padding: 10px 10px 6px; margin-bottom: 2px; display: flex; align-items: center; gap: 6px; }
.dx-nav-label::after { content: ""; flex: 1; height: 1px; background: rgba(255,255,255,0.18); }
.dx-nav-label-foot { padding: 0 10px 8px; margin-bottom: 0; }
/* Clickable group heading — expands/collapses that group's items */
.dx-nav-label-btn { width: 100%; border: none; background: transparent; cursor: pointer; font-family: inherit; border-radius: 5px; transition: color 0.15s ease; }
.dx-nav-label-btn::after { display: none; }
.dx-nav-label-btn span { flex: 1; text-align: left; }
.dx-nav-label-btn:hover { color: #ffffff; }
.dx-nav-label-chevron { font-size: 0.75rem; opacity: 0.75; flex-shrink: 0; transition: opacity 0.15s ease; }
.dx-nav-label-btn:hover .dx-nav-label-chevron { opacity: 1; }
.dx-nav-stack { display: grid; gap: 1px; }

.dx-nav-item { width: 100%; display: flex; align-items: center; gap: 9px; padding: 9px 10px; border-radius: 7px; border: 1px solid transparent; background: transparent; color: rgba(255,255,255,0.8); font-size: 0.82rem; font-weight: 500; cursor: pointer; transition: all 0.18s ease; position: relative; font-family: inherit; text-align: left; overflow: hidden; }
.dx-light .dx-nav-item, .dx-dark .dx-nav-item { color: rgba(255,255,255,0.8); }
.dx-nav-item:hover { background: rgba(255,255,255,0.1); color: #ffffff; border-color: rgba(255,255,255,0.15); }
.dx-light .dx-nav-item:hover, .dx-dark .dx-nav-item:hover { background: rgba(255,255,255,0.1); color: #ffffff; }
.dx-nav-item.active { background: #ffffff; color: #003380; border-color: transparent; font-weight: 700; box-shadow: 0 2px 8px rgba(0,0,0,0.15); }
.dx-light .dx-nav-item.active, .dx-dark .dx-nav-item.active { background: #ffffff; color: #003380; }
.dx-nav-icon { width: 30px; height: 30px; min-width: 30px; border-radius: 8px; display: flex; align-items: center; justify-content: center; font-size: 0.85rem; transition: all 0.2s ease; background: var(--dx-item-tint, rgba(255,255,255,0.12)); color: var(--dx-item-color, rgba(255,255,255,0.85)); border: none; }
.dx-nav-item:hover .dx-nav-icon { filter: brightness(1.25); }
.dx-nav-item.active .dx-nav-icon { background: var(--dx-item-color, #0047AB); color: #ffffff; box-shadow: 0 2px 6px rgba(0,0,0,0.18); }
.dx-nav-arrow { width: 18px; height: 18px; min-width: 18px; display: flex; align-items: center; justify-content: center; color: rgba(255,255,255,0.3); opacity: 0; transform: translateX(-4px); transition: all 0.18s ease; font-size: 0.65rem; }
.dx-nav-item:hover .dx-nav-arrow { opacity: 1; transform: translateX(0); color: rgba(255,255,255,0.6); }
.dx-nav-item.active .dx-nav-arrow { opacity: 1; transform: translateX(0); color: var(--dx-item-color, #0047AB); }
.dx-nav-indicator { position: absolute; left: 0; top: 50%; transform: translateY(-50%); width: 3px; height: 60%; border-radius: 0 3px 3px 0; background: var(--dx-item-color, #FAD201); }
.dx-nav-label-text { flex: 1; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

.dx-sidebar-foot { padding: 12px; position: relative; z-index: 1; margin-top: auto; border-top: 1px solid rgba(255,255,255,0.1); flex-shrink: 0; }
.dx-logout-btn { width: 100%; padding: 10px 14px; border-radius: 7px; border: 1px solid rgba(255,255,255,0.15); background: rgba(255,255,255,0.08); color: rgba(255,255,255,0.8); font-size: 0.82rem; font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 10px; transition: all 0.2s ease; font-family: inherit; }
.dx-light .dx-logout-btn, .dx-dark .dx-logout-btn { border-color: rgba(255,255,255,0.15); background: rgba(255,255,255,0.08); color: rgba(255,255,255,0.8); }
.dx-logout-btn:hover { background: rgba(192,57,43,0.3); border-color: rgba(192,57,43,0.5); color: #fff; }

.dx-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); backdrop-filter: blur(4px); -webkit-backdrop-filter: blur(4px); z-index: 190; animation: dxFadeUp 0.2s ease; }
.dx-light .dx-overlay, .dx-dark .dx-overlay { background: rgba(0,0,0,0.5); }

.dx-main { flex: 1; margin-left: 260px; display: flex; flex-direction: column; min-height: 100vh; min-height: 100dvh; position: relative; z-index: 1; min-width: 0; }

/* GOVERNMENT TOP BAR */
.dx-topbar { padding: 0 24px; border-bottom: 3px solid #FAD201; display: flex; align-items: center; gap: 10px; position: sticky; top: 0; z-index: 100; background: #0047AB; box-shadow: 0 2px 10px rgba(0,71,171,0.2); min-height: 58px; }
.dx-light .dx-topbar, .dx-dark .dx-topbar { background: #0047AB; border-bottom: 3px solid #FAD201; box-shadow: 0 2px 10px rgba(0,71,171,0.2); }
.dx-topbar::after { display: none; }
.dx-light .dx-topbar::after { display: none; }

.dx-hamburger { width: 38px; height: 38px; border-radius: 6px; border: 1px solid rgba(255,255,255,0.2); background: rgba(255,255,255,0.1); color: #fff; font-size: 1.2rem; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: all 0.2s ease; flex-shrink: 0; }
.dx-light .dx-hamburger, .dx-dark .dx-hamburger { border-color: rgba(255,255,255,0.2); background: rgba(255,255,255,0.1); color: #fff; }
.dx-hamburger:hover { background: rgba(255,255,255,0.2); }

.dx-topbar-info { flex: 1; min-width: 0; display: grid; gap: 2px; }
.dx-topbar-badge { width: fit-content; max-width: 100%; display: inline-flex; align-items: center; gap: 5px; padding: 3px 9px; border-radius: 4px; border: 1px solid rgba(250,210,1,0.3); background: rgba(250,210,1,0.12); color: #FAD201; font-size: 0.62rem; font-weight: 700; letter-spacing: 0.06em; }
.dx-light .dx-topbar-badge, .dx-dark .dx-topbar-badge { border-color: rgba(250,210,1,0.3); background: rgba(250,210,1,0.12); color: #FAD201; }
.dx-topbar-badge i { color: #FAD201; font-size: 0.78rem; }
.dx-page-title { font-size: clamp(0.95rem, 2vw, 1.15rem); font-weight: 700; color: #ffffff; margin: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; letter-spacing: 0.01em; }
.dx-light .dx-page-title, .dx-dark .dx-page-title { color: #ffffff; }
.dx-page-sub { font-size: 0.7rem; color: rgba(255,255,255,0.6); margin: 1px 0 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.dx-light .dx-page-sub, .dx-dark .dx-page-sub { color: rgba(255,255,255,0.6); }

.dx-topbar-actions { display: flex; gap: 6px; flex-shrink: 0; align-items: center; }

/* Search bar in topbar */
.dx-topbar-search { display: flex; align-items: center; gap: 6px; padding: 0 10px; background: rgba(255,255,255,0.12); border: 1px solid rgba(255,255,255,0.2); border-radius: 6px; height: 36px; max-width: 200px; transition: var(--dx-transition); }
.dx-light .dx-topbar-search, .dx-dark .dx-topbar-search { background: rgba(255,255,255,0.12); border-color: rgba(255,255,255,0.2); }
.dx-topbar-search:focus-within { border-color: #FAD201; box-shadow: 0 0 0 2px rgba(250,210,1,0.2); }
.dx-topbar-search i { color: rgba(255,255,255,0.6); font-size: 0.8rem; flex-shrink: 0; }
.dx-topbar-search input { border: none; background: none; color: #fff; font-size: 0.82rem; outline: none; width: 130px; font-family: inherit; }
.dx-light .dx-topbar-search input, .dx-dark .dx-topbar-search input { color: #fff; }
.dx-topbar-search input::placeholder { color: rgba(255,255,255,0.45); }

.dx-icon-btn { width: 36px; height: 36px; border-radius: 6px; border: 1px solid rgba(255,255,255,0.2); background: rgba(255,255,255,0.1); color: rgba(255,255,255,0.85); font-size: 0.9rem; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: all 0.18s ease; position: relative; flex-shrink: 0; }
.dx-light .dx-icon-btn, .dx-dark .dx-icon-btn { border-color: rgba(255,255,255,0.2); background: rgba(255,255,255,0.1); color: rgba(255,255,255,0.85); }
.dx-icon-btn:hover { background: rgba(255,255,255,0.2); color: #fff; }
.dx-light .dx-icon-btn:hover, .dx-dark .dx-icon-btn:hover { background: rgba(255,255,255,0.2); color: #fff; }

.dx-export-icon { border-color: rgba(32,96,61,0.4); color: #4ade80; background: rgba(32,96,61,0.2); }
.dx-export-icon:hover { background: rgba(32,96,61,0.35); }

/* Notification dropdown */
.dx-notif-wrapper { position: relative; }
.dx-notif-badge { position: absolute; top: -4px; right: -4px; width: 16px; height: 16px; border-radius: 50%; background: #FAD201; color: #003380; font-size: 8px; font-weight: 800; display: flex; align-items: center; justify-content: center; border: 2px solid #0047AB; }
.dx-light .dx-notif-badge, .dx-dark .dx-notif-badge { border-color: #0047AB; }

.dx-dropdown { position: absolute; top: calc(100% + 8px); right: 0; width: 280px; background: #ffffff; border: 1px solid rgba(0,71,171,0.15); border-radius: 8px; box-shadow: 0 8px 32px rgba(0,71,171,0.15); z-index: 500; animation: dxPop 0.2s ease; overflow: hidden; }
.dx-light .dx-dropdown, .dx-dark .dx-dropdown { background: #ffffff; border-color: rgba(0,71,171,0.15); box-shadow: 0 8px 32px rgba(0,71,171,0.15); }
.dx-dropdown-head { display: flex; align-items: center; justify-content: space-between; padding: 11px 14px; border-bottom: 1px solid #e2e8f0; font-size: 0.82rem; font-weight: 700; color: #1e293b; background: #f8fafc; }
.dx-light .dx-dropdown-head, .dx-dark .dx-dropdown-head { border-bottom-color: #e2e8f0; color: #1e293b; background: #f8fafc; }
.dx-dropdown-count { font-size: 0.65rem; background: #f1f5f9; color: #475569; padding: 2px 7px; border-radius: 4px; font-weight: 700; }
.dx-light .dx-dropdown-count, .dx-dark .dx-dropdown-count { background: #f1f5f9; color: #475569; }
.dx-dropdown-item { display: flex; align-items: center; gap: 10px; padding: 9px 14px; cursor: pointer; transition: var(--dx-transition); font-size: 0.8rem; color: #4a5568; }
.dx-light .dx-dropdown-item, .dx-dark .dx-dropdown-item { color: #4a5568; }
.dx-dropdown-item:hover { background: #f0f4f8; color: #0d1b2a; }
.dx-light .dx-dropdown-item:hover, .dx-dark .dx-dropdown-item:hover { background: #f0f4f8; color: #0d1b2a; }
.dx-dropdown-item i { font-size: 0.85rem; width: 18px; text-align: center; color: #64748b; }
.dx-dropdown-dot { width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0; }
.dx-dropdown-item-body { flex: 1; min-width: 0; }
.dx-dropdown-item-text { display: block; font-size: 0.8rem; font-weight: 500; color: #0d1b2a; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.dx-light .dx-dropdown-item-text, .dx-dark .dx-dropdown-item-text { color: #0d1b2a; }
.dx-dropdown-item-time { display: block; font-size: 0.65rem; color: #718096; margin-top: 2px; }
.dx-dropdown-divider { height: 1px; background: #e8edf3; margin: 4px 0; }
.dx-light .dx-dropdown-divider, .dx-dark .dx-dropdown-divider { background: #e8edf3; }
.dx-dropdown-danger { color: #c0392b !important; }
.dx-dropdown-danger:hover { background: rgba(192,57,43,0.06) !important; color: #c0392b !important; }

/* Profile button */
.dx-profile-btn { display: flex; align-items: center; gap: 7px; padding: 4px 10px 4px 4px; border: 1px solid rgba(255,255,255,0.2); background: rgba(255,255,255,0.12); border-radius: 6px; cursor: pointer; transition: var(--dx-transition); font-family: inherit; color: #fff; }
.dx-light .dx-profile-btn, .dx-dark .dx-profile-btn { border-color: rgba(255,255,255,0.2); background: rgba(255,255,255,0.12); color: #fff; }
.dx-profile-btn:hover { background: rgba(255,255,255,0.2); }
.dx-avatar { width: 28px; height: 28px; border-radius: 5px; background: #FAD201; color: #003380; display: flex; align-items: center; justify-content: center; font-size: 0.65rem; font-weight: 800; flex-shrink: 0; }
.dx-profile-name { font-size: 0.76rem; font-weight: 600; color: #fff; }
.dx-profile-chevron { font-size: 0.6rem; opacity: 0.5; color: #fff; }

/* CONTENT AREA */
.dx-content { flex: 1; width: 100%; max-width: 1600px; margin: 0 auto; padding: clamp(16px, 2.5vw, 24px); padding-bottom: 60px; position: relative; z-index: 1; background: #f0f4f8; }
.dx-dashboard { animation: dxFadeUp 0.4s ease; }

/* Stats Grid */
.dx-stats-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin-bottom: 24px; }
.dx-stat-card { background: #ffffff; border: 1px solid #e2e8f0; border-left: 4px solid #e2e8f0; border-radius: 8px; padding: 18px 16px; display: flex; align-items: center; gap: 14px; transition: all 0.25s ease; position: relative; overflow: hidden; cursor: default; animation: dxCardIn 0.4s ease both; box-shadow: 0 1px 4px rgba(0,0,0,0.06); }
.dx-light .dx-stat-card, .dx-dark .dx-stat-card { background: #ffffff; border: 1px solid #e2e8f0; border-left: 4px solid #e2e8f0; box-shadow: 0 1px 4px rgba(0,0,0,0.06); }
.dx-stat-card:hover { transform: translateY(-3px); border-left-color: #0047AB; box-shadow: 0 6px 18px rgba(0,0,0,0.1); }
.dx-light .dx-stat-card:hover, .dx-dark .dx-stat-card:hover { border-left-color: #0047AB; box-shadow: 0 6px 18px rgba(0,0,0,0.1); }
.dx-stat-glow { display: none; }
.dx-stat-icon { width: 48px; height: 48px; min-width: 48px; border-radius: 8px; display: flex; align-items: center; justify-content: center; font-size: 1.15rem; flex-shrink: 0; transition: all 0.25s ease; }
.dx-stat-card:hover .dx-stat-icon { transform: scale(1.06); }
.dx-stat-value { font-size: clamp(1.4rem, 2.5vw, 1.8rem); font-weight: 800; color: #0d1b2a; line-height: 1; letter-spacing: -0.02em; }
.dx-light .dx-stat-value, .dx-dark .dx-stat-value { color: #0d1b2a; }
.dx-stat-label { font-size: 0.66rem; color: #4a5568; font-weight: 600; text-transform: uppercase; letter-spacing: 0.07em; margin-top: 4px; }
.dx-light .dx-stat-label, .dx-dark .dx-stat-label { color: #4a5568; }

.dx-section { margin-bottom: 24px; }
.dx-section-head { margin-bottom: 14px; display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
.dx-section-title { font-size: 1rem; font-weight: 700; color: #0d1b2a; display: flex; align-items: center; margin: 0; letter-spacing: 0; }
.dx-light .dx-section-title, .dx-dark .dx-section-title { color: #0d1b2a; }

/* Quick Actions */
.dx-quick-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px; }
.dx-quick-btn { display: flex; align-items: center; gap: 10px; padding: 12px 14px; border-radius: 7px; background: #ffffff; border: 1px solid #e2e8f0; color: #374151; font-size: 0.82rem; font-weight: 600; cursor: pointer; transition: all 0.2s ease; font-family: inherit; text-align: left; animation: dxCardIn 0.4s ease both; box-shadow: 0 1px 3px rgba(0,0,0,0.05); }
.dx-light .dx-quick-btn, .dx-dark .dx-quick-btn { background: #ffffff; border-color: #e2e8f0; color: #374151; box-shadow: 0 1px 3px rgba(0,0,0,0.05); }
.dx-quick-btn:hover { background: #f8fafc; border-color: #94a3b8; color: #0d1b2a; transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0,0,0,0.08); }
.dx-light .dx-quick-btn:hover, .dx-dark .dx-quick-btn:hover { background: #f8fafc; border-color: #94a3b8; color: #0d1b2a; }
.dx-quick-icon { width: 36px; height: 36px; min-width: 36px; border-radius: 7px; display: flex; align-items: center; justify-content: center; font-size: 1rem; flex-shrink: 0; transition: all 0.2s ease; }
.dx-quick-btn:hover .dx-quick-icon { transform: scale(1.06); }
.dx-quick-arrow { margin-left: auto; font-size: 0.7rem; opacity: 0.3; transition: all 0.18s ease; }
.dx-quick-btn:hover .dx-quick-arrow { opacity: 0.7; transform: translateX(3px); }

/* Quick Actions — grouped rows (click a group to reveal its tiles) */
.dx-qa-groups { display: flex; flex-direction: column; gap: 6px; }
.dx-qa-group { border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden; background: #ffffff; }
.dx-light .dx-qa-group, .dx-dark .dx-qa-group { background: #ffffff; border-color: #e2e8f0; }
.dx-qa-group-head { width: 100%; display: flex; align-items: center; gap: 10px; padding: 10px 12px; border: none; background: transparent; cursor: pointer; font-family: inherit; text-align: left; transition: background 0.15s ease; }
.dx-qa-group-head:hover { background: #f8fafc; }
.dx-light .dx-qa-group-head:hover, .dx-dark .dx-qa-group-head:hover { background: #f8fafc; }
.dx-qa-group-icon { width: 34px; height: 34px; min-width: 34px; border-radius: 7px; display: flex; align-items: center; justify-content: center; font-size: 0.95rem; flex-shrink: 0; }
.dx-qa-group-label { flex: 1; font-size: 0.84rem; font-weight: 700; color: #1e3a5f; }
.dx-light .dx-qa-group-label, .dx-dark .dx-qa-group-label { color: #1e3a5f; }
.dx-qa-group-count { font-size: 0.68rem; font-weight: 700; color: #94a3b8; background: #f1f5f9; padding: 2px 8px; border-radius: 20px; }
.dx-qa-group-chevron { font-size: 0.7rem; color: #94a3b8; flex-shrink: 0; }
.dx-qa-group .dx-quick-grid { padding: 0 10px 10px; }

/* Charts */
.dx-charts-row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 18px; }
.dx-chart-card { background: #ffffff; border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden; animation: dxCardIn 0.4s ease both; box-shadow: 0 1px 4px rgba(0,0,0,0.06); }
.dx-light .dx-chart-card, .dx-dark .dx-chart-card { background: #ffffff; border-color: #e2e8f0; box-shadow: 0 1px 4px rgba(0,0,0,0.06); }
.dx-chart-card-head { display: flex; align-items: center; justify-content: space-between; padding: 14px 16px 10px; border-bottom: 1px solid #e2e8f0; background: #f8fafc; }
.dx-light .dx-chart-card-head, .dx-dark .dx-chart-card-head { border-bottom-color: #e2e8f0; background: #f8fafc; }
.dx-chart-title { font-size: 0.85rem; font-weight: 700; color: #1e293b; display: flex; align-items: center; gap: 7px; margin: 0; }
.dx-light .dx-chart-title, .dx-dark .dx-chart-title { color: #1e293b; }
.dx-chart-title i { font-size: 0.9rem; }
.dx-chart-badge { font-size: 0.6rem; font-weight: 600; color: #20603D; background: rgba(32,96,61,0.1); padding: 2px 8px; border-radius: 4px; border: 1px solid rgba(32,96,61,0.2); }
.dx-light .dx-chart-badge, .dx-dark .dx-chart-badge { color: #20603D; background: rgba(32,96,61,0.1); border-color: rgba(32,96,61,0.2); }
.dx-chart-card > svg, .dx-chart-card > div:last-child { padding: 10px 14px 14px; }
.dx-chart-body { padding: 12px 16px 16px; }
.dx-empty-chart { padding: 32px 20px; text-align: center; color: #718096; font-size: 0.82rem; }
.dx-pie-row { display: flex; gap: 14px; flex-wrap: wrap; justify-content: center; }
.dx-pie-mini { text-align: center; }
.dx-pie-mini-label { font-size: 0.7rem; font-weight: 600; color: #4a5568; margin-bottom: 8px; }
.dx-light .dx-pie-mini-label, .dx-dark .dx-pie-mini-label { color: #4a5568; }

/* Data Table */
.dx-table-wrap { background: #ffffff; border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden; box-shadow: 0 1px 4px rgba(0,0,0,0.06); }
.dx-light .dx-table-wrap, .dx-dark .dx-table-wrap { background: #ffffff; border-color: #e2e8f0; box-shadow: 0 1px 4px rgba(0,0,0,0.06); }
.dx-table-controls { display: flex; align-items: center; justify-content: space-between; padding: 12px 16px; border-bottom: 1px solid #e2e8f0; background: #f8fafc; gap: 10px; flex-wrap: wrap; }
.dx-light .dx-table-controls, .dx-dark .dx-table-controls { border-bottom-color: #e2e8f0; background: #f8fafc; }
.dx-table-search { display: flex; align-items: center; gap: 6px; padding: 0 10px; background: #ffffff; border: 1px solid #e2e8f0; border-radius: 5px; height: 32px; transition: var(--dx-transition); }
.dx-light .dx-table-search, .dx-dark .dx-table-search { background: #ffffff; border-color: #e2e8f0; }
.dx-table-search:focus-within { border-color: #64748b; box-shadow: 0 0 0 2px rgba(100,116,139,0.1); }
.dx-table-search i { color: #718096; font-size: 0.78rem; flex-shrink: 0; }
.dx-table-search input { border: none; background: none; color: #0d1b2a; font-size: 0.8rem; outline: none; width: 160px; font-family: inherit; }
.dx-light .dx-table-search input, .dx-dark .dx-table-search input { color: #0d1b2a; }
.dx-table-search input::placeholder { color: #718096; }
.dx-table-count { font-size: 0.74rem; color: #4a5568; font-weight: 600; }
.dx-export-table-btn { display: flex; align-items: center; gap: 6px; padding: 6px 12px; background: rgba(32,96,61,0.08); border: 1px solid rgba(32,96,61,0.2); border-radius: 5px; color: #20603D; font-size: 0.76rem; font-weight: 600; cursor: pointer; transition: var(--dx-transition); font-family: inherit; }
.dx-export-table-btn:hover { background: rgba(32,96,61,0.14); }
.dx-table-scroll { overflow-x: auto; }
.dx-table { width: 100%; border-collapse: collapse; font-size: 0.8rem; }
.dx-table th { text-align: left; padding: 10px 16px; font-size: 0.65rem; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 0.07em; border-bottom: 1px solid #e2e8f0; white-space: nowrap; background: #f8fafc; }
.dx-light .dx-table th, .dx-dark .dx-table th { color: #64748b; border-bottom-color: #e2e8f0; background: #f8fafc; }
.dx-th-sortable { cursor: pointer; user-select: none; transition: var(--dx-transition); }
.dx-th-sortable:hover { color: #1e293b; }
.dx-light .dx-th-sortable:hover, .dx-dark .dx-th-sortable:hover { color: #1e293b; }
.dx-th-sortable span { margin-right: 4px; }
.dx-th-sortable i { font-size: 0.65rem; opacity: 0.6; }
.dx-table td { padding: 10px 16px; border-bottom: 1px solid #f1f5f9; vertical-align: middle; }
.dx-light .dx-table td, .dx-dark .dx-table td { border-bottom-color: #f1f5f9; }
.dx-table tbody tr { transition: var(--dx-transition); }
.dx-table tbody tr:hover { background: #f0f4f8; }
.dx-light .dx-table tbody tr:hover, .dx-dark .dx-table tbody tr:hover { background: #f0f4f8; }
.dx-table tbody tr:last-child td { border-bottom: none; }
.dx-table-empty { text-align: center; padding: 28px 20px !important; color: #718096; }
.dx-table-user { display: flex; align-items: center; gap: 9px; }
.dx-table-avatar { width: 28px; height: 28px; border-radius: 5px; display: flex; align-items: center; justify-content: center; font-size: 0.75rem; font-weight: 700; flex-shrink: 0; }
.dx-table-user > span { font-weight: 500; color: #0d1b2a; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 180px; }
.dx-light .dx-table-user > span, .dx-dark .dx-table-user > span { color: #0d1b2a; }
.dx-table-type { font-size: 0.72rem; font-weight: 600; color: #475569; padding: 2px 8px; border-radius: 4px; background: #f1f5f9; }
.dx-light .dx-table-type, .dx-dark .dx-table-type { color: #475569; background: #f1f5f9; }
.dx-table-gender { font-size: 0.74rem; color: #4a5568; }
.dx-table-actions { display: flex; gap: 4px; }
.dx-table-action { width: 26px; height: 26px; border-radius: 5px; border: none; background: #f1f5f9; color: #64748b; cursor: pointer; display: flex; align-items: center; justify-content: center; font-size: 0.75rem; transition: var(--dx-transition); }
.dx-light .dx-table-action, .dx-dark .dx-table-action { background: #f1f5f9; color: #64748b; }
.dx-table-action:hover { background: #e2e8f0; color: #1e293b; }
.dx-light .dx-table-action:hover, .dx-dark .dx-table-action:hover { background: #e2e8f0; color: #1e293b; }
.dx-table-action-danger:hover { background: rgba(192,57,43,0.1); color: #c0392b; }

/* Pagination */
.dx-pagination { display: flex; align-items: center; justify-content: center; gap: 4px; padding: 12px 16px; border-top: 1px solid #f1f5f9; }
.dx-light .dx-pagination, .dx-dark .dx-pagination { border-top-color: #f1f5f9; }
.dx-page-btn { min-width: 30px; height: 30px; border: 1px solid #e2e8f0; background: none; color: #64748b; cursor: pointer; border-radius: 5px; display: flex; align-items: center; justify-content: center; font-size: 0.76rem; font-weight: 500; transition: var(--dx-transition); font-family: inherit; }
.dx-light .dx-page-btn, .dx-dark .dx-page-btn { border-color: #e2e8f0; color: #64748b; }
.dx-page-btn:hover:not(:disabled) { background: #f1f5f9; color: #1e293b; border-color: #94a3b8; }
.dx-light .dx-page-btn:hover:not(:disabled), .dx-dark .dx-page-btn:hover:not(:disabled) { background: #f1f5f9; color: #1e293b; }
.dx-page-btn:disabled { opacity: 0.25; cursor: not-allowed; }
.dx-page-active { background: #0047AB !important; color: #fff !important; border-color: #0047AB !important; }

/* Activity + Notifications Panels */
.dx-bottom-row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.dx-panel { background: #ffffff; border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden; box-shadow: 0 1px 4px rgba(0,0,0,0.06); }
.dx-light .dx-panel, .dx-dark .dx-panel { background: #ffffff; border-color: #e2e8f0; box-shadow: 0 1px 4px rgba(0,0,0,0.06); }
.dx-panel-head { display: flex; align-items: center; justify-content: space-between; padding: 13px 16px; border-bottom: 1px solid #e2e8f0; background: #f8fafc; }
.dx-light .dx-panel-head, .dx-dark .dx-panel-head { border-bottom-color: #e2e8f0; background: #f8fafc; }
.dx-panel-title { font-size: 0.88rem; font-weight: 700; color: #1e293b; display: flex; align-items: center; gap: 7px; margin: 0; }
.dx-light .dx-panel-title, .dx-dark .dx-panel-title { color: #1e293b; }

.dx-activity-list { padding: 4px 0; }
.dx-activity-item { display: flex; gap: 10px; padding: 9px 16px; transition: var(--dx-transition); }
.dx-activity-item:hover { background: #f0f4f8; }
.dx-light .dx-activity-item:hover, .dx-dark .dx-activity-item:hover { background: #f0f4f8; }
.dx-activity-dot { width: 8px; height: 8px; border-radius: 50%; margin-top: 5px; flex-shrink: 0; }
.dx-activity-body { flex: 1; min-width: 0; }
.dx-activity-text { font-size: 0.8rem; font-weight: 500; color: #0d1b2a; line-height: 1.4; }
.dx-light .dx-activity-text, .dx-dark .dx-activity-text { color: #0d1b2a; }
.dx-activity-time { display: flex; align-items: center; gap: 4px; font-size: 0.65rem; color: #718096; margin-top: 3px; }

.dx-notif-panel-list { padding: 4px 0; }
.dx-notif-panel-item { display: flex; gap: 10px; padding: 9px 16px; transition: var(--dx-transition); }
.dx-notif-panel-item:hover { background: #f0f4f8; }
.dx-light .dx-notif-panel-item:hover, .dx-dark .dx-notif-panel-item:hover { background: #f0f4f8; }
.dx-notif-panel-icon { width: 32px; height: 32px; border-radius: 6px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; font-size: 0.85rem; }
.dx-notif-panel-body { flex: 1; min-width: 0; }
.dx-notif-panel-title { font-size: 0.8rem; font-weight: 500; color: #0d1b2a; }
.dx-light .dx-notif-panel-title, .dx-dark .dx-notif-panel-title { color: #0d1b2a; }
.dx-notif-panel-time { font-size: 0.65rem; color: #718096; margin-top: 3px; }

/* BOTTOM NAVIGATION (Mobile) */
.dx-bottom-nav { position: fixed; bottom: 10px; left: 10px; right: 10px; background: #0047AB; border: 1px solid rgba(250,210,1,0.3); border-radius: 12px; box-shadow: 0 8px 28px rgba(0,71,171,0.25); display: flex; justify-content: space-around; padding: 8px 8px calc(8px + env(safe-area-inset-bottom, 0px)); z-index: 10000; }
.dx-light .dx-bottom-nav, .dx-dark .dx-bottom-nav { background: #0047AB; border-color: rgba(250,210,1,0.3); }
.dx-bottom-item { display: flex; flex-direction: column; align-items: center; gap: 3px; padding: 7px 8px; border: none; background: none; color: rgba(255,255,255,0.65); font-size: 0.56rem; font-weight: 600; cursor: pointer; transition: all 0.2s ease; position: relative; font-family: inherit; min-width: 0; flex: 1; border-radius: 8px; }
.dx-light .dx-bottom-item, .dx-dark .dx-bottom-item { color: rgba(255,255,255,0.65); }
.dx-bottom-item i { font-size: 1.1rem; transition: transform 0.2s ease; }
.dx-bottom-item span { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 56px; }
.dx-bottom-item.active { color: #FAD201; background: rgba(250,210,1,0.12); }
.dx-light .dx-bottom-item.active, .dx-dark .dx-bottom-item.active { color: #FAD201; background: rgba(250,210,1,0.12); }
.dx-bottom-item.active i { transform: scale(1.1) translateY(-2px); }
.dx-bottom-item:hover:not(.active) { color: rgba(255,255,255,0.9); background: rgba(255,255,255,0.08); }
.dx-bottom-dot { position: absolute; top: 4px; width: 4px; height: 4px; border-radius: 50%; background: #FAD201; animation: dxPulse 2s ease-in-out infinite; }

/* Toast */
.dx-toast { position: fixed; bottom: 90px; left: 50%; transform: translateX(-50%); padding: 11px 18px; border-radius: 7px; display: flex; align-items: center; gap: 10px; font-size: 0.82rem; font-weight: 500; z-index: 2000; animation: dxPop 0.3s cubic-bezier(0.34,1.56,0.64,1); max-width: 90vw; border: 1px solid; box-shadow: 0 4px 18px rgba(0,0,0,0.15); }
.dx-toast-ok { background: rgba(32,96,61,0.1); border-color: rgba(32,96,61,0.3); color: #20603D; }
.dx-toast-err { background: rgba(192,57,43,0.1); border-color: rgba(192,57,43,0.3); color: #c0392b; }
.dx-toast-icon { font-size: 1rem; flex-shrink: 0; }
.dx-toast-x { background: none; border: none; color: inherit; cursor: pointer; opacity: 0.5; font-size: 0.8rem; margin-left: 6px; transition: opacity 0.2s; }
.dx-toast-x:hover { opacity: 1; }

/* Modal */
.dx-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.55); backdrop-filter: blur(6px); -webkit-backdrop-filter: blur(6px); z-index: 1500; display: flex; align-items: center; justify-content: center; padding: 16px; animation: dxFadeUp 0.2s ease; }
.dx-modal { background: #ffffff; border: 1px solid #e2e8f0; border-radius: 10px; padding: 32px 24px; text-align: center; max-width: 380px; width: 100%; animation: dxScale 0.25s ease; box-shadow: 0 12px 40px rgba(0,0,0,0.12); }
.dx-light .dx-modal, .dx-dark .dx-modal { background: #ffffff; border-color: #e2e8f0; }
.dx-modal-icon { width: 58px; height: 58px; border-radius: 10px; background: rgba(192,57,43,0.1); color: #c0392b; display: inline-flex; align-items: center; justify-content: center; font-size: 1.5rem; margin-bottom: 16px; }
.dx-modal-title { font-size: 1.1rem; font-weight: 800; color: #0d1b2a; margin-bottom: 8px; }
.dx-light .dx-modal-title, .dx-dark .dx-modal-title { color: #0d1b2a; }
.dx-modal-desc { font-size: 0.85rem; color: #4a5568; margin-bottom: 22px; line-height: 1.5; }
.dx-light .dx-modal-desc, .dx-dark .dx-modal-desc { color: #4a5568; }
.dx-modal-actions { display: flex; gap: 10px; }
.dx-modal-btn { flex: 1; padding: 11px 18px; border-radius: 7px; font-weight: 650; font-size: 0.85rem; cursor: pointer; transition: all 0.2s ease; display: flex; align-items: center; justify-content: center; gap: 8px; font-family: inherit; border: none; }
.dx-modal-cancel { background: #f8fafc; color: #475569; border: 1px solid #e2e8f0 !important; }
.dx-light .dx-modal-cancel, .dx-dark .dx-modal-cancel { background: #f8fafc; color: #475569; border-color: #e2e8f0 !important; }
.dx-modal-cancel:hover { background: #e8edf3; color: #0d1b2a; }
.dx-modal-confirm { background: #c0392b; color: #fff; box-shadow: 0 3px 12px rgba(192,57,43,0.25); }
.dx-modal-confirm:hover { background: #a93226; transform: translateY(-1px); box-shadow: 0 6px 18px rgba(192,57,43,0.3); }

/* Loader */
.dx-loader-screen { min-height: 100vh; min-height: 100dvh; display: flex; flex-direction: column; align-items: center; justify-content: center; background: #f0f4f8; gap: 18px; }
.dx-loader-ring { width: 48px; height: 48px; border-radius: 50%; border: 3px solid rgba(0,71,171,0.15); border-top-color: #0047AB; border-right-color: #FAD201; animation: dxSpin 0.75s linear infinite; box-shadow: 0 0 14px rgba(0,71,171,0.15); }
.dx-loader-text { font-size: 0.84rem; color: #4a5568; font-weight: 500; letter-spacing: 0.02em; }

/* RESPONSIVE */
@media (min-width: 1500px) {
  .dx-stats-grid { grid-template-columns: repeat(6, 1fr); }
}
@media (max-width: 1200px) {
  .dx-stats-grid { grid-template-columns: repeat(3, 1fr); }
}
@media (max-width: 991px) {
  .dx-sidebar { transform: translateX(calc(-100% - 20px)); width: 280px; min-width: 280px; }
  .dx-sidebar.open { transform: translateX(0); animation: dxSlideIn 0.3s ease; }
  .dx-main { margin-left: 0 !important; padding-bottom: 90px; width: 100%; }
  .dx-stats-grid { grid-template-columns: repeat(2, 1fr); }
  .dx-quick-grid { grid-template-columns: repeat(2, 1fr); }
  .dx-charts-row { grid-template-columns: 1fr; }
  .dx-bottom-row { grid-template-columns: 1fr; }
}
@media (max-width: 767px) {
  .dx-topbar { padding: 0 12px; }
  /* Section is already named by the page title; the pill only crowds the
     header and clips under the status bar on short viewports. */
  .dx-topbar-badge { display: none; }
  /* Clear the fixed bottom nav (+ its shadow + safe-area) so the last
     card/record is never trapped underneath it. .dx-content owns this
     clearance at phone width, so drop the .dx-main padding that would
     otherwise stack on top of it. */
  .dx-main { padding-bottom: 0; }
  .dx-content { padding: 12px 10px calc(120px + env(safe-area-inset-bottom, 0px)); }
  .dx-stats-grid { grid-template-columns: repeat(2, 1fr); gap: 8px; }
  .dx-quick-grid { grid-template-columns: 1fr; gap: 7px; }
  .dx-stat-card { padding: 13px 11px; }
  .dx-stat-icon { width: 40px; height: 40px; min-width: 40px; font-size: 0.95rem; border-radius: 7px; }
  .dx-stat-value { font-size: 1.25rem; }
  .dx-page-title { font-size: 0.95rem; }
  .dx-topbar-search { max-width: 130px; }
  .dx-topbar-search input { width: 70px; }
  .dx-profile-name { display: none; }
}
@media (max-width: 576px) {
  .dx-stats-grid { grid-template-columns: repeat(2, 1fr); gap: 7px; }
  .dx-sidebar { width: 275px; min-width: 275px; }
  .dx-stat-card { padding: 11px 10px; border-radius: 7px; gap: 9px; }
  .dx-stat-icon { width: 36px; height: 36px; min-width: 36px; font-size: 0.9rem; border-radius: 6px; }
  .dx-stat-value { font-size: 1.1rem; }
  .dx-stat-label { font-size: 0.56rem; }
  .dx-quick-btn { padding: 11px 12px; border-radius: 7px; }
  .dx-page-title { font-size: 0.9rem; }
  .dx-topbar-badge { font-size: 0.58rem; padding: 3px 8px; }
  .dx-bottom-item i { font-size: 1.05rem; }
  .dx-bottom-item span { font-size: 0.5rem; }
  .dx-modal { padding: 24px 18px; border-radius: 9px; }
  .dx-toast { bottom: 100px; padding: 9px 14px; }
  .dx-topbar-actions { gap: 4px; }
  .dx-topbar-search { display: none; }
  .dx-icon-btn { width: 32px; height: 32px; }
  .dx-section-title { font-size: 0.9rem; }
  .dx-chart-card-head { padding: 11px 13px 8px; }
}
@media (max-width: 400px) {
  .dx-stat-value { font-size: 1rem; }
  .dx-stat-label { font-size: 0.52rem; }
  .dx-stats-grid { gap: 6px; }
}

/* SPECIAL */
@supports (-webkit-touch-callout: none) { .dx-shell { min-height: 100dvh; } }
@media (hover: none) and (pointer: coarse) {
  .dx-stat-card:hover { transform: none; box-shadow: none; }
  .dx-quick-btn:hover { transform: none; }
  .dx-icon-btn:hover { transform: none; }
}
@media (display-mode: standalone) { .dx-bottom-nav { padding-bottom: calc(10px + env(safe-area-inset-bottom, 0px)); } }
@media (prefers-reduced-motion: reduce) { *, *::before, *::after { animation-duration: 0.01ms !important; animation-iteration-count: 1 !important; transition-duration: 0.01ms !important; } }
`}</style>
);

export default DashboardIsibo;